import RunHistorySaveWarning from '../components/common/RunHistorySaveWarning';
import { isResourceDeleting, assertResourceAvailable } from '../utils/resourceLifecycle';
import ResourceDeletionNotice from '../components/common/ResourceDeletionNotice';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import useNow from '../hooks/useNow';
import {
  Paper,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  MenuItem,
  Menu,
  Chip,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FunctionsIcon from '@mui/icons-material/Functions';
import BoltIcon from '@mui/icons-material/Bolt';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import { db } from '../services/firebase';
import { collection, doc, getDocs, setDoc, runTransaction, getDoc, onSnapshot, query, orderBy, limit, where } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useAttributeOptions } from '../contexts/AttributesContext';
import FunctionBuilderDialog from '../components/functions/FunctionBuilderDialog';
import CompositeOutputTabs from '../components/common/CompositeOutputTabs';
import ViewBuilder from '../components/views/ViewBuilder';
import ViewDisplay from '../components/views/ViewDisplay';
import AutomationEditor from '../components/automations/AutomationEditor';
import CreateAutomationDialog from '../components/automations/CreateAutomationDialog';
import SidebarFooter from '../components/common/SidebarFooter';
import LeftSidebar from '../components/common/LeftSidebar';
import { authenticatedFetch } from '../services/api_utils';
import { functionEndpoint } from '../services/endpoints';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PublishFunctionDialog from '../components/functions/PublishFunctionDialog';
import {
  cleanRangeValue,
  compareParameters,
  formatParameterInputValue,
  isDateRangeParam,
  prepareParameterControls,
  formatParameterErrors,
  resolveParameterType,
  emptyParameterValue,
  isEmptyParameterValue,
  filterSignature,
  hiddenDisagreements,
  findInvertedDateRange,
} from '../utils/functionParameterUtils';
import { isExternalTable } from '../utils/tableTypes';
import { isTableUsable } from '../utils/tableLifecycle';
import { blockedRecipients, confirmationChanged, recipientEntry } from '../utils/emailConfirmation';
import DeleteResourceDialog from '../components/common/DeleteResourceDialog';
import LoadFailure, { loadFailureMessage } from '../components/common/LoadFailure';
import ParameterField from '../components/common/ParameterField';
import { controlsFromRunParameters } from '../utils/viewCards';
import DateDraftScope, { INCOMPLETE_DATES_MESSAGE } from '../components/common/DateDraftScope';
import useDeleteConfirmation from '../components/common/useDeleteConfirmation';
import { isEmailFeatureEnabled } from '../utils/outputRegistry';
import { list as listContacts } from '../services/emailContacts';
import { loadEmailRetryDetail } from '../services/emailLedger';
import {
  describeWhen,
  listAutomations,
} from '../services/functionTriggers';
import {
  isFunctionRunnable,
  isPublishedVersion,
  loadRunTarget,
  prepareHistoricalRunParameters,
  versionBlockNames,
  loadVersionBlockNames,
  versionDataSourceBlocks,
  versionEmailOutputs,
  attachmentLabel,
  versionHasExternalEffect,
} from '../services/functionVersions';
import {
  ACTIVITY_PAGE_SIZE,
  latestCompletedRun,
  loadAllRuns,
  loadFunctionRuns,
  loadLatestRunsByFunction,
  loadRun,
} from '../services/functionRuns';
import { isRunSettled, runOutputsSummary, submittedEmailReceipts } from '../utils/runProjection';
import useRunResultHydration from '../hooks/useRunResultHydration';
import {
  ORIGIN_FILTERS,
  OUTCOME_FILTERS,
  deriveRunOutcome,
  formatRunTime,
  formatRunTimeShort,
  matchesOriginFilter,
  matchesOutcomeFilter,
  outcomeDisplay,
  outcomeDotColor,
  runOriginLabel,
  runCreatedAt,
  runExportStamp,
} from '../utils/runDisplay';
import { RunOriginIcon, RunOutcomeGlyph } from '../components/common/RunChrome';
import { exportFileName } from '../utils/excelExport';
import { formatText } from '../components/common/TextFormatterProvider';

// Run the public run_data_function endpoint with the explicit request envelope
// (section 3.11). The backend NO LONGER accepts flat parameters — the whole
// envelope (execution_mode, origin, view_context, nested parameters, and the
// confirmation/retry fields) is posted as-is. Error responses carry a machine
// `code`, so the HTTP status and code are attached to the thrown error:
//   - 428 external_effect_confirmation_required → re-surface the confirmation
//   - 409 output_not_retryable → disable that retry control with the reason
async function runFunctionEndpoint(envelope) {
  const response = await authenticatedFetch(functionEndpoint('run_data_function'), {
    method: 'POST',
    body: JSON.stringify(envelope)
  });

  if (!response.ok) {
    const text = await response.text();
    let payload = null;
    try { payload = text ? JSON.parse(text) : null; } catch (parseErr) { payload = null; }
    const message = (payload && (payload.message || payload.error)) || text || `Request failed with status ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.code = payload && payload.code ? payload.code : null;
    throw error;
  }

  return response.json();
}

const toCompositeResult = result => ({
  historySaveUnconfirmed: result?.persist_error != null,
  outputs: result?.outputs && typeof result.outputs === 'object' ? result.outputs : {},
  truncatedTables: Array.isArray(result?.truncated_tables) ? result.truncated_tables : [],
});

const FUNCTION_RESULT_TARGET = 'function-pane';

const isPlainObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);

// Sidebar tab indices. Views leads because a dashboard is what the page is for;
// Functions is the machinery behind it, Automations is that machinery running
// unattended, and Activity is the audit trail. Named constants because every
// panel, placeholder and post-save redirect keys off these numbers.
const TAB_VIEWS = 0;
const TAB_FUNCTIONS = 1;
const TAB_AUTOMATIONS = 2;
const TAB_ACTIVITY = 3;

// Sidebar tab labels only render when the Tabs strip is at least this wide.
// Below it, the four fullWidth tabs leave too little room for the longest
// label ("Automations" at 0.7rem ≈ 65px + padding ≈ 73px per tab) and
// neighbouring labels collide, so the tabs fall back to icons with tooltips.
const TAB_LABELS_MIN_WIDTH = 300;

const STAGE_STATUS_DISPLAY = {
  not_started: { label: 'Not started', color: 'default' },
  running: { label: 'Running', color: 'info' },
  succeeded: { label: 'Succeeded', color: 'success' },
  pending: { label: 'Pending', color: 'info' },
  warning: { label: 'Warning', color: 'warning' },
  partial_success: { label: 'Partial', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
};

// Email domain status → chip colour for the per-output delivery panel.
const EMAIL_STATUS_COLOR = {
  pending: 'info',
  delivered: 'success',
  succeeded: 'success',
  warning: 'warning',
  partial_success: 'warning',
  failed: 'error',
};

// A recipient's delivery status (the backend's DeliveryStatus enum) → what a
// reader sees. Terminal failures read as errors, the in-flight and unconfirmed
// states as pending or warning — the severities the Email tab's table uses —
// and no internal name ever reaches the screen.
const DELIVERY_STATUS_DISPLAY = {
  planned: { label: 'Queued', color: 'info' },
  submitting: { label: 'Sending', color: 'info' },
  pending: { label: 'Pending', color: 'info' },
  submission_unknown: { label: 'Not confirmed sent', color: 'warning' },
  delivered: { label: 'Delivered', color: 'success' },
  temporary_failure: { label: 'Delayed', color: 'warning' },
  permanent_failure: { label: 'Failed', color: 'error' },
  complained: { label: 'Reported as spam', color: 'error' },
  delivery_unknown: { label: 'Delivery unconfirmed', color: 'warning' },
  submission_rejected: { label: 'Rejected', color: 'error' },
};
const deliveryStatusDisplay = (status) => DELIVERY_STATUS_DISPLAY[status] || {
  label: status ? String(status).replace(/_/g, ' ') : 'Unknown',
  color: 'default',
};

// A failed run produced no result, so its stage chips and error are all there is
// to show. Re-running it would not reproduce anything either.
const runFailed = (run) => run?.status === 'failed' || deriveRunOutcome(run) === 'failed';

// Collect the email Output entries from a composite result, overlaying the live
// run-doc `outputs_summary` (authoritative for delivery status / retry
// eligibility) when available.
const collectEmailOutputs = (outputsMap, summaryMap) => {
  const outputs = isPlainObject(outputsMap) ? outputsMap : {};
  const summaries = isPlainObject(summaryMap) ? summaryMap : {};
  const collected = [];
  const seen = new Set();

  Object.entries(outputs).forEach(([blockId, output]) => {
    if (output?.output_type !== 'email') return;
    const summary = summaries[blockId] || {};
    seen.add(blockId);
    collected.push({
      blockId,
      nodeName: summary.node_name || output.node_name || 'Email output',
      status: summary.status || output.status || 'unknown',
      retryEligible: (summary.retry_eligible ?? output.retry_eligible) === true,
      recipients: Array.isArray(output.recipients) ? output.recipients : [],
      attachmentNames: output.attachment_names || summary.attachment_names || [],
      missingAttachments: output.missing_attachments || summary.missing_attachments || [],
    });
  });

  // Include email outputs that exist only in the live run-doc summary (e.g. the
  // rematerialised result.json has not been reloaded yet).
  Object.entries(summaries).forEach(([blockId, summary]) => {
    if (summary?.output_type !== 'email' || seen.has(blockId)) return;
    collected.push({
      blockId,
      nodeName: summary.node_name || 'Email output',
      status: summary.status || 'unknown',
      retryEligible: summary.retry_eligible === true,
      recipients: [],
      attachmentNames: summary.attachment_names || [],
      missingAttachments: summary.missing_attachments || [],
    });
  });

  return collected;
};

// Resolve the effect confirmation from the PUBLISHED VERSION this run will
// execute, plus the company contact book. The draft is never consulted, so what
// the user confirms is exactly what runs. Returns null when the version has no
// Email Output.
async function buildEmailConfirmation(companyName, versionContent, functionName = '') {
  if (!companyName) return null;
  const emailOutputs = versionEmailOutputs(versionContent);
  if (emailOutputs.length === 0) return null;

  const contacts = await listContacts(companyName);
  const contactsById = new Map();
  (contacts || []).forEach((contact) => contactsById.set(contact.id || contact.contact_id, contact));

  // Every recipient as the contact book has it now: name and address, or
  // the reason it cannot receive mail (M76).
  const outputs = emailOutputs.map((output) => ({
    blockId: output.blockId,
    nodeName: formatText(output.nodeName),
    subject: output.subject,
    recipients: output.recipientContactIds.map((id) => recipientEntry(id, contactsById.get(id))),
    attachments: output.attachments.map((attachment) => attachmentLabel(attachment, formatText)),
  }));

  return { outputs, functionName: formatText(functionName || '') };
}

// Retrieval/Logic/Output stage chips for a run detail view (section 3.6). Only
// rendered when the run carries stage fields (legacy runs do not).
function StageChips({ run }) {
  const stages = [
    ['Retrieval', run.retrieval_status],
    ['Logic', run.logic_status],
    ['Output', run.output_status],
  ];
  if (stages.every(([, status]) => !status)) return null;
  return (
    <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
      {stages.map(([label, status]) => {
        const display = STAGE_STATUS_DISPLAY[status] || { label: status || '—', color: 'default' };
        return (
          <Chip
            key={label}
            size="small"
            variant="outlined"
            color={display.color}
            label={`${label}: ${display.label}`}
          />
        );
      })}
    </Stack>
  );
}

// One row of the run picker: origin icon · relative time · origin label ·
// outcome glyph · pinned version. The pinned version matters because a run
// executes the version it captured, not whatever is published now.
function RunPickerRow({ run, selected, onSelect }) {
  const outcome = deriveRunOutcome(run);
  return (
    <MenuItem selected={selected} onClick={() => onSelect(run)} sx={{ gap: 1, py: 0.75 }}>
      <RunOriginIcon triggerType={run.trigger_type} fontSize={14} sx={{ color: 'text.secondary' }} />
      <Typography variant="body2" sx={{ flex: 1, whiteSpace: 'nowrap' }}>
        {formatRunTime(run.created_at)} · {runOriginLabel(run.trigger_type)}
      </Typography>
      <RunOutcomeGlyph outcome={outcome} fontSize={13} />
      <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
        v{run.function_version ?? '—'}
      </Typography>
    </MenuItem>
  );
}

// The cross-function run feed. The same paginated all-runs query as before, with
// origin and outcome filters applied client-side over the pages already loaded —
// paging back loads more rows, it never re-queries with a server-side filter.
// How many of the newest runs the Activity tab watches live. Enough that a run
// starting or finishing while the feed is on screen is seen at once; small
// enough to be one cheap listener. Everything older pages in on demand.
const ACTIVITY_HEAD_LIMIT = 20;

// A filtered Activity feed first asks Firestore directly whether — and roughly
// how many — runs match. Origin is one field (`trigger_type`), so an equality
// query on it needs no composite index; it just cannot be ordered, and its
// answer is complete only when it holds fewer than this many: then fewer than a
// page of matches exist at all, and they are shown sorted here. This many or
// more means the newest page of matches has to come from the ordered stream,
// which is scanned forward in pages of ACTIVITY_SCAN_PAGE until a page's worth
// is on screen or the history ends. Outcome is derived from two fields (see
// deriveRunOutcome) and cannot be probed: with an origin probe in hand it is
// applied in memory; alone, it goes straight to the scan.
const ACTIVITY_PROBE_LIMIT = ACTIVITY_PAGE_SIZE + 1;
const ACTIVITY_SCAN_PAGE = 100;

// The feed with the live head folded in: a run already listed is replaced by
// its newer document, a new one is added, and the whole stays newest-first.
const mergeActivityHead = (runs, head) => {
  const byId = new Map(runs.map((run) => [run.id, run]));
  head.forEach((run) => byId.set(run.id, run));
  return Array.from(byId.values()).sort((a, b) => (
    (runCreatedAt(b)?.getTime() || 0) - (runCreatedAt(a)?.getTime() || 0)
  ));
};

function ActivityFeed({
  runs,
  // The live function names, so a renamed function's history reads under one
  // name whatever a run's document stored at the time (M201).
  functionNameById = {},
  loading,
  error,
  onRetry,
  hasMore,
  onLoadMore,
  onSelectRun,
  originFilter,
  outcomeFilter,
  onOriginFilter,
  onOutcomeFilter,
  // A filter is set, the runs on screen hold fewer matches than a page, and
  // older history is still being read for more (see the Dashboard's scan).
  searching = false,
}) {
  useNow();
  const filtered = originFilter !== 'all' || outcomeFilter !== 'all';
  const visible = runs.filter((run) => (
    matchesOriginFilter(run, originFilter) && matchesOutcomeFilter(run, outcomeFilter)
  ));

  return (
    <Paper
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 1.5,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1.5, display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 500 }}>Activity</Typography>
        <Typography variant="caption" color="text.secondary">
          All runs, all functions, all origins
        </Typography>
      </Box>

      <Box
        sx={{
          px: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexWrap: 'wrap',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {ORIGIN_FILTERS.map((filter) => (
          <Chip
            key={`origin-${filter.value}`}
            size="small"
            label={filter.label}
            variant={originFilter === filter.value ? 'filled' : 'outlined'}
            color={originFilter === filter.value ? 'primary' : 'default'}
            onClick={() => onOriginFilter(filter.value)}
          />
        ))}
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        {OUTCOME_FILTERS.map((filter) => (
          <Chip
            key={`outcome-${filter.value}`}
            size="small"
            label={filter.label}
            variant={outcomeFilter === filter.value ? 'filled' : 'outlined'}
            color={outcomeFilter === filter.value ? 'primary' : 'default'}
            onClick={() => onOutcomeFilter(filter.value)}
          />
        ))}
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List disablePadding>
          {visible.map((run) => (
            <ListItem
              key={run.id}
              onClick={() => onSelectRun(run)}
              sx={{
                cursor: 'pointer',
                gap: 1.5,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <RunOutcomeGlyph outcome={deriveRunOutcome(run)} fontSize={14} />
              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {functionNameById[run.function_id] || run.function_name || run.function_id}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                icon={<RunOriginIcon triggerType={run.trigger_type} fontSize={12} sx={{ ml: 0.75 }} />}
                label={runOriginLabel(run.trigger_type)}
                sx={{ height: 20, fontSize: '0.68rem' }}
              />
              <Typography variant="caption" color="text.disabled" sx={{ width: 34, textAlign: 'right' }}>
                v{run.function_version ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ width: 110, textAlign: 'right' }}>
                {formatRunTime(run.created_at)}
              </Typography>
            </ListItem>
          ))}
        </List>

        {error && !loading && <LoadFailure thing="recent runs" onRetry={onRetry} sx={{ p: 3 }} />}

        {visible.length === 0 && !loading && !searching && !error && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            {runs.length === 0 && !filtered
              ? 'No runs yet. Run a function to see its history here.'
              : 'No runs match these filters.'}
          </Typography>
        )}

        {searching && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, p: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Looking for matching runs…</Typography>
          </Box>
        )}

        {loading && !searching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {hasMore && runs.length > 0 && !loading && !searching && (
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
            <Button size="small" onClick={onLoadMore}>Load more</Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// Per-output email delivery panel: status, recipient detail, and the output-only
// retry action. A previous rejection is feedback, not a permanent ban: the
// next confirmation rechecks contacts that may since have been reactivated.
function EmailOutputsPanel({ emailOutputs, onRetry, retryBusy, retryFeedback }) {
  if (!emailOutputs || emailOutputs.length === 0) return null;
  return (
    <Stack spacing={1.5} sx={{ mb: 2 }}>
      {emailOutputs.map((output) => {
        const feedback = retryFeedback[output.blockId];
        const busy = Boolean(retryBusy[output.blockId]);
        const canRetry = output.retryEligible;
        // Logical eligibility survives contact changes; confirmation checks
        // whether any of these recipients can receive the retry now.
        const showRetry = output.retryEligible;
        const tooltip = feedback || '';
        return (
          <Paper
            key={output.blockId}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 1.5 }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">{output.nodeName}</Typography>
                <Chip
                  size="small"
                  label={output.status}
                  color={EMAIL_STATUS_COLOR[output.status] || 'default'}
                  variant="outlined"
                />
              </Stack>
              {showRetry && (
                <Tooltip title={tooltip}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busy || !canRetry}
                      onClick={() => onRetry(output.blockId)}
                    >
                      {busy ? 'Retrying…' : 'Retry original email'}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
            {output.recipients.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                {output.recipients.map((recipient) => {
                  const delivery = deliveryStatusDisplay(recipient.status);
                  return (
                    <Chip
                      key={recipient.contact_id || recipient.display_name}
                      size="small"
                      variant="outlined"
                      color={delivery.color}
                      label={`${recipient.display_name || recipient.contact_id}: ${delivery.label}`}
                    />
                  );
                })}
              </Stack>
            )}
            {output.attachmentNames.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Attachments: {output.attachmentNames.join(', ')}
              </Typography>
            )}
            {output.missingAttachments.length > 0 && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                Unavailable: {output.missingAttachments.join(', ')}
              </Typography>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}

// Confirmation shown before an effectful manual run. Its confirm control is
// disabled from click until the backend responds (prevents a double send).
// `changed`: the contact book moved since this confirmation was first shown,
// so it was rebuilt and must be read again (H38). `error`: the recheck itself
// failed, and nothing runs until it succeeds. A recipient that cannot receive
// mail is named with the fix and blocks Confirm (M76).
function EffectConfirmationDialog({ open, confirmation, changed = false, error = '', busy, onConfirm, onCancel }) {
  const outputs = confirmation?.outputs || [];
  const blocked = blockedRecipients(confirmation);
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {confirmation?.functionName ? `Send email and run ${confirmation.functionName}` : 'Send email and run function'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This function sends one or more emails when it runs. Please confirm the details below before it runs.
        </Typography>
        {changed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Recipient details changed since this confirmation was prepared. Review them and confirm again.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {outputs.map((output) => (
          <Box
            key={output.blockId}
            sx={{ mb: 2, p: 1.5, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{output.nodeName}</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              <strong>To:</strong> {output.recipients.map((recipient) => recipient.label).join(', ') || '—'}
            </Typography>
            {output.recipients.filter((recipient) => recipient.blocked).map((recipient) => (
              <Typography key={recipient.id} variant="body2" color="error" sx={{ wordBreak: 'break-word' }}>
                {`${recipient.label}: ${recipient.blocked}`}
              </Typography>
            ))}
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              <strong>Subject:</strong> {output.subject || '—'}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              <strong>Attachments:</strong> {output.attachments.join(', ') || '—'}
            </Typography>
          </Box>
        ))}
        {blocked.length > 0 ? (
          <Alert severity="error">
            {`${blocked.length === 1 ? 'A recipient' : `${blocked.length} recipients`} cannot receive this email, so it cannot run. ${INACTIVE_RUN_HINT}`}
          </Alert>
        ) : (
          <Alert severity="info">
            Emails are sent to external recipients. Acceptance by a recipient&apos;s mail server does not guarantee inbox placement.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" disabled={busy || blocked.length > 0}>
          {busy ? 'Sending…' : 'Confirm and run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const INACTIVE_RUN_HINT = "Edit this Function's Email Output and publish it again, or restore the contact in Settings.";

// Confirmation for `Retry original email`. Everything shown here comes from the
// authoritative Email ledger — the historical subject, the attachment filenames
// that were actually sent, and every ORIGINAL recipient address with the status
// its delivery reached. Current contacts control availability only; they never
// change historical addresses. The current Function configuration is not used.
function RetryEmailDialog({ open, detail, error, busy, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Retry original email</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : !detail ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Loading the original email…</Typography>
          </Stack>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This re-sends the email this run already created. It does not run the function again,
              so no data is recomputed and no new attachments are built.
            </Typography>
            <Box sx={{ mb: 2, p: 1.5, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                <strong>Subject:</strong> {detail.finalSubject || '—'}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                <strong>Attachments:</strong> {detail.attachmentNames.join(', ') || '—'}
              </Typography>
              {detail.missingAttachments.length > 0 && (
                <Typography variant="body2" color="warning.main" sx={{ wordBreak: 'break-word' }}>
                  <strong>Unavailable at send time:</strong> {detail.missingAttachments.join(', ')}
                </Typography>
              )}
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Original recipients</Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              {detail.recipients.map((recipient) => (
                <Stack
                  key={`${recipient.contactId}:${recipient.address}`}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ flexWrap: 'wrap' }}
                >
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {recipient.displayName} &lt;{recipient.address}&gt;
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={deliveryStatusDisplay(recipient.status).color}
                    label={deliveryStatusDisplay(recipient.status).label}
                  />
                  <Typography variant="caption" color={recipient.retryEligible ? 'text.secondary' : 'warning.main'}>
                    {recipient.retryEligible ? 'Included in retry' : recipient.unavailableReason}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Alert severity="info">
              Decode will send only the addresses above that are still retry-eligible and whose
              contact is still active. That set can be smaller than the list shown, but it can never
              include an address outside this email.
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" disabled={busy || !detail?.retryAvailable || Boolean(error)}>
          {busy ? 'Sending…' : 'Confirm retry'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// The centre pane with nothing selected. One grey sentence floating near the top
// reads as a page that failed to load rather than as a starting point, so the
// empty pane names what this tab holds, says in one line what it is for, and
// offers the single action that resolves it.
function CentrePaneZeroState({ icon, heading, copy, actionLabel, onAction, disabled = false }) {
  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1.25,
        p: 4,
      }}
    >
      {icon}
      <Typography variant="h5">{heading}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, lineHeight: 1.7 }}>
        {copy}
      </Typography>
      <Button variant="contained" color="primary" onClick={onAction} disabled={disabled} sx={{ mt: 1 }}>
        {actionLabel}
      </Button>
    </Box>
  );
}

function Dashboard() {
  const { companyName, currentUser } = useAuth();
  // The sidebar's "14m" / "Yest" stamps, the run picker's and the selected
  // run's provenance are relative, so the page follows the shared clock.
  useNow();
  const [functions, setFunctions] = useState([]);
  const [publishingFunctionId, setPublishingFunctionId] = useState(null);
  const [draftFunctions, setDraftFunctions] = useState([]);
  const [finalFunctions, setFinalFunctions] = useState([]);
  const [, setPredefinedFunctions] = useState([]);
  const [, setCustomFunctions] = useState([]);
  const [views, setViews] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedFunctionId, setSelectedFunctionId] = useState(null);
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [functionDoc, setFunctionDoc] = useState(null);
  // The published version this function will run: one head read plus one version
  // read, captured when the function is selected and re-captured after a 409.
  // Everything run-facing — the Run control, the parameter panel, and the effect
  // confirmation — derives from this, never from the draft.
  const [runTarget, setRunTarget] = useState(null);
  const runTargetRef = useRef(runTarget);
  runTargetRef.current = runTarget;
  // True from the moment a function is selected until its run target has landed
  // or failed. The pane is deliberately empty in between (see
  // handleSelectFunction); this is what puts a spinner there instead.
  const [functionLoading, setFunctionLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewLoadGeneration, setViewLoadGeneration] = useState(0);
  // The lease identity of ONE editing session in this tab: minted when the
  // builder opens, dropped when it closes. It lives here, above the dialog,
  // because the dialog is remounted by development refreshes and by any
  // re-render that drops it — a remount must REJOIN the session it interrupted
  // rather than compete with it for its own lease. It is dropped on close
  // because every draft write is authorised by the session id alone
  // (utils/functionMutation.js), so an editor displaced by a takeover must
  // never come back under the identity the lease still names.
  const [builderSession, setBuilderSession] = useState(null);
  const showBuilder = builderSession !== null;
  const openBuilder = useCallback(() => setBuilderSession({ sessionId: crypto.randomUUID() }), []);
  const closeBuilder = useCallback(() => setBuilderSession(null), []);
  // One New Function click creates one draft: every entry point is disabled
  // while the creation is in flight, and a second click meanwhile is ignored.
  const [creatingFunction, setCreatingFunction] = useState(false);
  const [createFunctionError, setCreateFunctionError] = useState('');
  // The View builder's editing session, on the same terms as the Function
  // builder's above: one identity per open, dropped on close.
  const [viewBuilderSession, setViewBuilderSession] = useState(null);
  const showViewBuilder = viewBuilderSession !== null;
  const openViewBuilder = useCallback(() => setViewBuilderSession({ sessionId: crypto.randomUUID() }), []);
  const closeViewBuilder = useCallback(() => setViewBuilderSession(null), []);
  const [parametersFormData, setParametersFormData] = useState({});
  // Unified is the default: most functions read one table per column, so one
  // control per column is the honest shape and the per-block groups are the
  // exception.
  const [unifyParameters, setUnifyParameters] = useState(true);
  const [excludedParams, setExcludedParams] = useState({});
  const preparedParameters = useMemo(() => prepareParameterControls(
    parametersFormData, functionDoc?.data_source_blocks, excludedParams,
  ), [parametersFormData, functionDoc, excludedParams]);
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError] = useState('');
  // Half-typed dates in the parameters panel (see AppDatePicker): Run is
  // refused while any exist; Clear remounts the controls to discard them.
  const dateScopeRef = useRef(null);
  const [hasDateDraft, setHasDateDraft] = useState(false);
  const [dateDraftError, setDateDraftError] = useState('');
  const [parameterClearGeneration, setParameterClearGeneration] = useState(0);
  const handleDateDraftChange = (partial) => {
    setHasDateDraft(partial);
    if (!partial) setDateDraftError('');
  };
  // Selecting a function and viewing one of its runs are no longer mutually
  // exclusive states: the function pane always shows SOME run of the selected
  // function when one exists. `viewedRunId` is that run, whether it came from
  // auto-loading the newest completed run, from the run picker, from the
  // Activity feed, or from a run this pane just started.
  const [viewedRunId, setViewedRunId] = useState(null);
  const [viewedRunData, setViewedRunData] = useState(null);
  const [functionRuns, setFunctionRuns] = useState([]);
  // History resumes from the last company-stream document read, skipping
  // empty batches until this Function has matches or the stream ends.
  const [functionRunsCursor, setFunctionRunsCursor] = useState(null);
  const [functionRunsHasMore, setFunctionRunsHasMore] = useState(false);
  const functionHistoryAbortRef = useRef(null);
  useEffect(() => () => functionHistoryAbortRef.current?.abort(), [companyName]);
  const [functionRunsLoading, setFunctionRunsLoading] = useState(false);
  const [runMenuAnchor, setRunMenuAnchor] = useState(null);
  // The newest run of every listed function, for the sidebar status dots.
  const [functionLastRuns, setFunctionLastRuns] = useState({});
  // Email effect confirmation + output-only retry state.
  const [pendingEmailRun, setPendingEmailRun] = useState(null);
  const [runConfirmBusy, setRunConfirmBusy] = useState(false);
  const [runPreparing, setRunPreparing] = useState(false);
  const [retryBusy, setRetryBusy] = useState({});
  const [retryFeedback, setRetryFeedback] = useState({});
  // The pending `Retry original email` confirmation, built from the Email ledger.
  const [retryRequest, setRetryRequest] = useState(null);
  const viewedRunIdRef = useRef(null);
  const selectedFunctionRef = useRef(null);
  const functionSelectionGenerationRef = useRef(0);
  const runRequestGenerationRef = useRef(0);
  const companyRef = useRef(companyName);
  companyRef.current = companyName;
  const acceptHydratedRunResult = useCallback(({ runId, data }) => {
    if (viewedRunIdRef.current !== runId) return false;
    setRunResult(toCompositeResult(data));
    return true;
  }, []);
  const {
    states: runResultHydrations,
    prepare: prepareRunResultHydration,
    hydrate: hydrateRunResult,
    retry: retryRunResultHydration,
    clear: clearRunResultHydration,
  } = useRunResultHydration({
    scopeKey: `${companyName || ''}::function::${selectedFunctionId || ''}`,
    onHydrated: acceptHydratedRunResult,
  });
  const functionDeletion = useDeleteConfirmation({
    resourceType: 'function',
    onPreviewError: (err) => setRunError(`Failed to check function dependencies: ${err.message}`),
    onDeleteError: (err) => setRunError(`Failed to delete function: ${err.message}`),
    onDeleted: (func) => {
      // Committed: the sidebar is a live listener and drops the entry itself.
      // The pane lets go of the function if it was showing it.
      if (selectedFunctionRef.current === func.id) {
        functionHistoryAbortRef.current?.abort();
        setFunctionRunsLoading(false);
        setSelectedFunctionId(null);
        selectedFunctionRef.current = null;
        setFunctionDoc(null);
        setRunResult(null);
        setViewedRunId(null);
        setViewedRunData(null);
        setFunctionRuns([]);
        setParametersFormData({});
        setExcludedParams({});
      }
    },
  });
  // A view is deleted through the same backend cascade as a function.
  const viewDeletion = useDeleteConfirmation({
    resourceType: 'view',
    onPreviewError: (err) => setRunError(`Failed to check view dependencies: ${err.message}`),
    onDeleteError: (err) => setRunError(`Failed to delete view: ${err.message}`),
    onDeleted: (view) => {
      if (selectedViewId === view.id) {
        setSelectedViewId(null);
        setViewData(null);
      }
    },
  });
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [functionToName, setFunctionToName] = useState(null);
  const [newFunctionName, setNewFunctionName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_VIEWS);
  // Icons-only first: at the default sidebar width the labels don't fit, so
  // starting hidden avoids a first-paint flash of colliding text.
  const [showTabLabels, setShowTabLabels] = useState(false);
  // A ResizeObserver rather than reading the width in render: a sidebar drag
  // writes the rail width straight to the DOM and renders nothing until
  // release, so this is the only signal that fires while the drag is live.
  // Callback ref because the Tabs unmount whenever the sidebar collapses.
  const tabsResizeObserverRef = useRef(null);
  const attachTabsNode = useCallback((node) => {
    if (tabsResizeObserverRef.current) {
      tabsResizeObserverRef.current.disconnect();
      tabsResizeObserverRef.current = null;
    }
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      const width = entry ? entry.contentRect.width : 0;
      setShowTabLabels(width >= TAB_LABELS_MIN_WIDTH);
    });
    observer.observe(node);
    tabsResizeObserverRef.current = observer;
  }, []);
  const [draftsExpanded, setDraftsExpanded] = useState(false);
  const [tableSchemas, setTableSchemas] = useState({});
  // The attribute columns the selected function's parameters read; their value
  // lists come live from the AttributesProvider.
  const [parameterAttributeColumns, setParameterAttributeColumns] = useState([]);
  const attributeValues = useAttributeOptions(parameterAttributeColumns);
  const [activityRuns, setActivityRuns] = useState([]);
  const [activityLastDoc, setActivityLastDoc] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityHasMore, setActivityHasMore] = useState(true);
  const [activityOriginFilter, setActivityOriginFilter] = useState('all');
  const [activityOutcomeFilter, setActivityOutcomeFilter] = useState('all');
  // The origin probe (see ACTIVITY_PROBE_LIMIT): { origin, runs, complete,
  // loading }, or null when no origin filter is set or the probe failed.
  const [activityProbe, setActivityProbe] = useState(null);
  // Automations: every trigger document in the company, the one selected, and
  // the published version that one executes. Loaded when the tab is opened and
  // re-read after every write, so the list and the editor never disagree.
  const [automations, setAutomations] = useState([]);
  // Reads that failed, by resource — the sidebar then says so in place of the
  // resource's empty state, keeping whatever it already listed. A listener that
  // errors has stopped, so Retry resubscribes (resourceAttempt is in their deps).
  const [resourceErrors, setResourceErrors] = useState({});
  const [resourceAttempt, setResourceAttempt] = useState(0);
  const [activityError, setActivityError] = useState(false);
  const [functionRunsError, setFunctionRunsError] = useState(false);
  const setResourceError = useCallback((key, failed) => {
    setResourceErrors((prev) => (Boolean(prev[key]) === failed ? prev : { ...prev, [key]: failed }));
  }, []);
  const retryResources = () => setResourceAttempt((n) => n + 1);
  const [selectedAutomationId, setSelectedAutomationId] = useState(null);
  const [automationTarget, setAutomationTarget] = useState(null);
  const [automationTargetError, setAutomationTargetError] = useState(false);
  // The target read is in flight: not yet "missing", not yet "failed".
  const [automationTargetLoading, setAutomationTargetLoading] = useState(false);
  const [automationContacts, setAutomationContacts] = useState([]);
  const [showCreateAutomation, setShowCreateAutomation] = useState(false);
  const selectedAutomationRef = useRef(null);

  const applyFunctions = useCallback((funcs) => {
    setFunctions(funcs);
    const drafts = funcs.filter(f => f.status === 'Draft');
    const finals = funcs.filter(f => f.status === 'Final');
    setDraftFunctions(drafts);
    setFinalFunctions(finals);
    // Split functions into predefined and custom
    setPredefinedFunctions(finals.filter(f => f.author !== 'user'));
    setCustomFunctions(finals.filter(f => f.author === 'user'));
  }, []);

  // The lists are live. An open Function keeps its captured version; an open
  // View receives saved definition changes as a reload notice, preserving its
  // local arrangement and drafts. Views also watch their Functions' versions.
  useEffect(() => {
    if (!companyName) return undefined;
    const unsubscribeFunctions = onSnapshot(
      collection(db, `users/${companyName}/data_functions`),
      (snapshot) => {
        setResourceError('functions', false);
        applyFunctions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      },
      (err) => {
        console.error('Failed to watch functions:', err);
        setResourceError('functions', true);
      },
    );
    const unsubscribeViews = onSnapshot(
      collection(db, `users/${companyName}/dashboard_views`),
      (snapshot) => {
        setResourceError('views', false);
        setViews(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      },
      (err) => {
        console.error('Failed to watch views:', err);
        setResourceError('views', true);
      },
    );
    // Tables too: a table created on the Data page or by the agent is offered
    // by the builder's data-source picker without a reload. One still being
    // created is not a table yet.
    const unsubscribeTables = onSnapshot(
      collection(db, `users/${companyName}/tables`),
      (snapshot) => {
        // A table whose deletion has begun, however that attempt ended, is not
        // a data source any more.
        setTables(snapshot.docs
          .map((tableDoc) => ({ id: tableDoc.id, ...tableDoc.data() }))
          .filter(isTableUsable));
      },
      (err) => console.error('Failed to watch tables:', err),
    );
    return () => {
      unsubscribeFunctions();
      unsubscribeViews();
      unsubscribeTables();
    };
  }, [companyName, applyFunctions, setResourceError, resourceAttempt]);

  useEffect(() => {
    if (functions.some((resource) => resource.id === selectedFunctionId && isResourceDeleting(resource) && resource.deletion_started_at)) {
      closeBuilder();
      setFunctionDoc(null);
      setRunTarget(null);
      setPendingEmailRun(null);
    }
    if (views.some((resource) => resource.id === selectedViewId && isResourceDeleting(resource) && resource.deletion_started_at)) {
      closeViewBuilder();
      setViewData(null);
    }
  }, [functions, views, selectedFunctionId, selectedViewId, closeBuilder, closeViewBuilder]);

  // The sidebar doubles as a status board: a dot per function says at a glance
  // that last night's scheduled run failed, without opening anything.
  //
  // It is filled from ONE read of the recent-runs window rather than a query per
  // function, because per-function filtering would need a composite index. The
  // trade is that a function with no run inside that window shows no dot at all;
  // a missing dot is cosmetic, so it is not worth a deeper scan each.
  //
  // Keyed on the set of function ids, not the live list itself: the list is a
  // new array on every function snapshot (a draft autosave anywhere), and the
  // dots only depend on which functions exist.
  const finalFunctionIdsSignature = useMemo(
    () => finalFunctions.map((func) => func.id).sort().join('|'),
    [finalFunctions],
  );
  useEffect(() => {
    if (!companyName || !finalFunctionIdsSignature) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { byFunction } = await loadLatestRunsByFunction(
          companyName,
          finalFunctionIdsSignature.split('|'),
        );
        if (cancelled) return;
        setFunctionLastRuns((prev) => {
          const next = { ...prev };
          Object.entries(byFunction).forEach(([functionId, entry]) => {
            if (entry.latest) next[functionId] = entry.latest;
          });
          return next;
        });
      } catch (err) {
        console.error('Failed to load recent runs for the sidebar:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [companyName, finalFunctionIdsSignature]);

  // The lists above are live, so these one-shot reloads are only a way to wait
  // for a write to be visible before acting on it.
  const refreshFunctions = useCallback(async () => {
    if (!companyName) return;
    const funcsRef = collection(db, `users/${companyName}/data_functions`);
    const funcsSnap = await getDocs(funcsRef);
    applyFunctions(funcsSnap.docs.map(doc => ({id: doc.id, ...doc.data()})));
  }, [companyName, applyFunctions]);

  const refreshViews = useCallback(async () => {
    if (!companyName) return;
    const viewsRef = collection(db, `users/${companyName}/dashboard_views`);
    const viewsSnap = await getDocs(viewsRef);
    const viewsList = viewsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setViews(viewsList);
  }, [companyName]);

  const loadActivity = useCallback(async (afterDoc = null, pageSize = ACTIVITY_PAGE_SIZE) => {
    if (!companyName) return;
    setActivityLoading(true);
    setActivityError(false);
    try {
      const { runs, lastDoc, hasMore } = await loadAllRuns(companyName, {
        pageSize,
        afterDoc,
      });
      setActivityRuns(prev => (afterDoc ? [...prev, ...runs] : runs));
      setActivityLastDoc(lastDoc);
      setActivityHasMore(hasMore);
    } catch (err) {
      console.error('Failed to load runs:', err);
      setActivityError(true);
    } finally {
      setActivityLoading(false);
    }
  }, [companyName]);

  // Reload the feed whenever the user navigates to the Activity tab.
  useEffect(() => {
    if (activeTab === TAB_ACTIVITY) {
      loadActivity();
    }
  }, [activeTab, loadActivity]);

  // The origin probe: one unordered equality read, re-run when the filter or
  // the company changes and dropped when the filter clears. A failed probe is
  // not an error the reader needs: the scan below answers instead.
  useEffect(() => {
    if (activeTab !== TAB_ACTIVITY || !companyName || activityOriginFilter === 'all') {
      setActivityProbe(null);
      return undefined;
    }
    let cancelled = false;
    const origin = activityOriginFilter;
    setActivityProbe({ origin, runs: [], complete: false, loading: true });
    getDocs(query(
      collection(db, `users/${companyName}/function_runs`),
      where('trigger_type', '==', origin),
      limit(ACTIVITY_PROBE_LIMIT),
    ))
      .then((snapshot) => {
        if (cancelled) return;
        const runs = snapshot.docs.map((runDoc) => ({ id: runDoc.id, ...runDoc.data(), _snap: runDoc }));
        setActivityProbe({
          origin,
          runs: mergeActivityHead([], runs),
          complete: runs.length < ACTIVITY_PROBE_LIMIT,
          loading: false,
        });
      })
      .catch((err) => {
        console.error('Failed to probe runs by origin:', err);
        if (!cancelled) setActivityProbe(null);
      });
    return () => { cancelled = true; };
  }, [activeTab, companyName, activityOriginFilter]);

  const activityFiltered = activityOriginFilter !== 'all' || activityOutcomeFilter !== 'all';
  const activityProbeComplete = Boolean(activityProbe?.complete);
  const activityMatchCount = useMemo(() => activityRuns.filter((run) => (
    matchesOriginFilter(run, activityOriginFilter) && matchesOutcomeFilter(run, activityOutcomeFilter)
  )).length, [activityRuns, activityOriginFilter, activityOutcomeFilter]);
  // Reading older history for matches: a filter is set, the probe did not
  // answer completely, the runs on screen hold fewer matches than a page, and
  // there is history left.
  const activitySearching = activeTab === TAB_ACTIVITY
    && activityFiltered
    && !activityProbeComplete
    && !activityError
    && activityHasMore
    && Boolean(activityLastDoc)
    && activityMatchCount < ACTIVITY_PAGE_SIZE;

  // The scan: one larger page at a time, until enough matches are on screen or
  // the stream ends. Each page changes the state this depends on, so the next
  // page follows on its own; nothing here re-reads the first page, which the
  // tab-entry load owns.
  useEffect(() => {
    if (!activitySearching || activityLoading || activityProbe?.loading) return;
    loadActivity(activityLastDoc, ACTIVITY_SCAN_PAGE);
  }, [activitySearching, activityLoading, activityProbe?.loading, activityLastDoc, loadActivity]);

  // While the tab is open, the head of the recency stream is watched too, so a
  // run that starts or finishes while the feed is on screen appears — and its
  // row updates as it progresses — without leaving and coming back.
  useEffect(() => {
    if (activeTab !== TAB_ACTIVITY || !companyName) return undefined;
    const unsubscribe = onSnapshot(
      query(
        collection(db, `users/${companyName}/function_runs`),
        orderBy('created_at', 'desc'),
        limit(ACTIVITY_HEAD_LIMIT),
      ),
      (snapshot) => {
        const head = snapshot.docs.map((runDoc) => ({ id: runDoc.id, ...runDoc.data(), _snap: runDoc }));
        setActivityRuns((prev) => mergeActivityHead(prev, head));
      },
      (err) => console.error('Failed to watch recent runs:', err),
    );
    return unsubscribe;
  }, [activeTab, companyName]);

  const refreshAutomations = useCallback(async () => {
    if (!companyName) return;
    try {
      setAutomations(await listAutomations(companyName));
    } catch (err) {
      console.error('Failed to load automations:', err);
    }
  }, [companyName]);

  // The Automations tab is the only surface that needs the whole trigger
  // collection or the contact book, so both are read when it is opened rather
  // than on every dashboard load. The trigger list stays live while the tab is
  // open, so a View creating or removing a managed automation shows at once.
  useEffect(() => {
    if (activeTab !== TAB_AUTOMATIONS || !companyName) return undefined;
    let cancelled = false;
    const unsubscribe = onSnapshot(
      collection(db, `users/${companyName}/function_triggers`),
      (snapshot) => {
        setResourceError('automations', false);
        setAutomations(snapshot.docs.map((triggerDoc) => ({ id: triggerDoc.id, ...triggerDoc.data() })));
      },
      (err) => {
        console.error('Failed to watch automations:', err);
        setResourceError('automations', true);
      },
    );
    if (isEmailFeatureEnabled()) {
      listContacts(companyName)
        .then((rows) => { if (!cancelled) setAutomationContacts(rows); })
        .catch((err) => console.error('Failed to load email contacts:', err));
    }
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [activeTab, companyName, setResourceError, resourceAttempt]);

  // Paint ONE run into the function pane. The bounded `result_preview` on the run
  // document paints synchronously and for free; the generation-guarded effect
  // below then downloads the complete result from storage and replaces it.
  const paintRun = useCallback((run) => {
    setRunError('');
    setRetryBusy({});
    setRetryFeedback({});
    setRetryRequest(null);
    if (!run) {
      clearRunResultHydration(FUNCTION_RESULT_TARGET);
      setViewedRunId(null);
      setViewedRunData(null);
      setRunResult(null);
      return;
    }
    prepareRunResultHydration(FUNCTION_RESULT_TARGET, run);
    setViewedRunId(run.id);
    setViewedRunData(run);
    setRunResult(run.result_preview ? toCompositeResult(run.result_preview) : null);
  }, [clearRunResultHydration, prepareRunResultHydration]);

  // Load the recent runs of one function and display one of them. `preferredRunId`
  // is set when the user arrived from the Activity feed; a run that is not on the
  // first page is fetched directly and shown alongside that page rather than
  // paging blindly until it appears.
  const loadRunsForFunction = useCallback(async (funcId, preferredRunId = null) => {
    if (!companyName || !funcId) return;
    functionHistoryAbortRef.current?.abort();
    const controller = new AbortController();
    functionHistoryAbortRef.current = controller;
    setFunctionRunsLoading(true);
    setFunctionRunsError(false);
    try {
      const { runs, cursor, hasMore } = await loadFunctionRuns(companyName, funcId, {
        signal: controller.signal,
      });
      // A slower response for a function the user has already navigated away
      // from must not overwrite the pane.
      if (controller.signal.aborted || selectedFunctionRef.current !== funcId) return;

      let list = runs;
      let target = null;
      if (preferredRunId) {
        target = list.find((run) => run.id === preferredRunId) || null;
        if (!target) {
          // A run reached from the Activity feed can be older than anything the
          // scan found, so it is read by document id and shown alongside.
          const loaded = await loadRun(companyName, preferredRunId);
          if (controller.signal.aborted || selectedFunctionRef.current !== funcId) return;
          if (loaded) {
            list = [loaded, ...list.filter((run) => run.id !== loaded.id)];
            target = loaded;
          }
        }
      }
      // The newest run that actually produced a result is what the pane opens
      // on. When every recent run failed there is nothing to display, so the
      // newest run is shown instead — its stage chips and error are the point.
      if (!target) target = latestCompletedRun(list) || list[0] || null;

      setFunctionRuns(list);
      setFunctionRunsCursor(cursor);
      setFunctionRunsHasMore(hasMore);
      paintRun(target);
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error('Failed to load runs for this function:', err);
      if (selectedFunctionRef.current === funcId) setFunctionRunsError(true);
    } finally {
      if (!controller.signal.aborted) setFunctionRunsLoading(false);
    }
  }, [companyName, paintRun]);

  const loadMoreFunctionRuns = useCallback(async () => {
    if (!companyName || !selectedFunctionId || !functionRunsCursor) return;
    functionHistoryAbortRef.current?.abort();
    const controller = new AbortController();
    functionHistoryAbortRef.current = controller;
    setFunctionRunsLoading(true);
    setFunctionRunsError(false);
    try {
      const { runs, cursor, hasMore } = await loadFunctionRuns(companyName, selectedFunctionId, {
        signal: controller.signal,
        cursor: functionRunsCursor,
      });
      if (controller.signal.aborted || selectedFunctionRef.current !== selectedFunctionId) return;
      setFunctionRuns(prev => {
        const known = new Set(prev.map((run) => run.id));
        return [...prev, ...runs.filter((run) => !known.has(run.id))];
      });
      setFunctionRunsCursor(cursor);
      setFunctionRunsHasMore(hasMore);
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error('Failed to load older runs:', err);
      setFunctionRunsError(true);
    } finally {
      if (!controller.signal.aborted) setFunctionRunsLoading(false);
    }
  }, [companyName, selectedFunctionId, functionRunsCursor]);

  const handleCreateFunction = async () => {
    if (!companyName || creatingFunction) return;
    setCreatingFunction(true);
    setCreateFunctionError('');
    try {
      await createFunctionDraft();
    } catch (err) {
      console.error('Failed to create a function:', err);
      setCreateFunctionError('Could not create a function. Try again.');
    } finally {
      setCreatingFunction(false);
    }
  };

  const createFunctionDraft = async () => {
    const id = `${Date.now()}_${Math.floor(Math.random()*1000)}`;

    const newFunctionData = {
      status: 'Draft',
      author: 'user',
      nodes: [],
      edges: [],
      data_source_blocks: {},
      logic_blocks: {},
      // A new function is a draft: nothing executes until its first publish.
      published_version: null,
      has_unpublished_changes: false,
      edit_lease: null,
    };

    await setDoc(doc(db, `users/${companyName}/data_functions`, id), newFunctionData);

    await refreshFunctions();

    setViewData(null);
    await handleSelectFunction(id, { skipHistory: true });
    if (selectedFunctionRef.current === id) openBuilder();
  };

  const handleCreateView = () => {
    functionHistoryAbortRef.current?.abort();
    setSelectedFunctionId(null);
    selectedFunctionRef.current = null;
    setSelectedViewId(null);
    setFunctionDoc(null);
    setViewData(null);
    setRunResult(null);
    setParametersFormData({});
    setExcludedParams({});
    openViewBuilder();
  };

  const handleSelectFunction = async (funcId, options = {}) => {
    functionHistoryAbortRef.current?.abort();
    const { runId = null, skipHistory = false } = options;
    const selectionGeneration = ++functionSelectionGenerationRef.current;
    runRequestGenerationRef.current += 1;
    setIsLoading(false);
    setFunctionRunsLoading(false);
    setSelectedFunctionId(funcId);
    selectedFunctionRef.current = funcId;
    setSelectedViewId(null);
    setViewData(null);
    setViewedRunId(null);
    setViewedRunData(null);
    setFunctionRuns([]);
    setFunctionRunsCursor(null);
    setFunctionRunsHasMore(false);
    setRunMenuAnchor(null);
    setRunResult(null);
    setRunError('');
    setTableSchemas({});
    setParameterAttributeColumns([]);
    setRetryRequest(null);
    // A confirmation prepared for the previous function describes that
    // function's emails; it must not stay open over the new selection (H47).
    setPendingEmailRun(null);
    // The previous function comes down before the new one is read. Left up, its
    // Run control stays live under the new highlight, and a run started then pairs
    // the new function's id with the old function's version and parameters (H46).
    // The pane and the parameter rail both render on functionDoc, so clearing it
    // is what removes them; functionLoading paints a spinner in their place.
    setFunctionDoc(null);
    setRunTarget(null);
    setParametersFormData({});
    setExcludedParams({});

    if (!companyName || !funcId) {
      setFunctionLoading(false);
      return;
    }
    setFunctionLoading(true);

    try {
      await loadSelectedFunction(funcId, runId, { skipHistory, selectionGeneration });
    } catch (err) {
      if (companyRef.current !== companyName || selectedFunctionRef.current !== funcId || functionSelectionGenerationRef.current !== selectionGeneration) return;
      console.error('Failed to load function:', err);
      setRunError('This function could not be loaded. Select it again to retry.');
    } finally {
      // A selection that moved on is a later call's responsibility; one that was
      // cleared entirely has nothing loading.
      if (functionSelectionGenerationRef.current === selectionGeneration) {
        setFunctionLoading(false);
      }
    }
  };

  // The reads behind one selection: run history, head, published version, then
  // the table schemas and tag values the parameter panel needs. Every step after
  // an await re-checks that funcId is still the selection.
  const loadSelectedFunction = async (funcId, runId, { skipHistory, selectionGeneration }) => {
    // The run history load does not depend on the version contract, so it starts
    // immediately and paints the pane while the parameter panel is still being
    // assembled below.
    if (!skipHistory) void loadRunsForFunction(funcId, runId);

    // One head read plus one version read. The parameter panel below is built
    // from that version's content, so the filters offered always belong to the
    // version this Run will execute.
    const target = await loadRunTarget(companyName, funcId);

    // A newer selection while this read was in flight makes this result stale.
    // Discarding it is what keeps the loaded version, the parameter form, and the
    // email confirmation built from them tied to the function that is actually
    // selected — otherwise a slow load for A can land under B and offer A's
    // recipients for approval (H-13). Same guard the run-history and lease
    // loaders in this file already use.
    if (companyRef.current !== companyName || selectedFunctionRef.current !== funcId || functionSelectionGenerationRef.current !== selectionGeneration) return;

    if (target) {
      const dataSourceBlocks = versionDataSourceBlocks(target.content);
      const fDoc = { ...target.head, data_source_blocks: dataSourceBlocks };
      setFunctionDoc(fDoc);
      setRunTarget(target);

      if (target.publishedVersion && target.content) {
        const newFormData = {};
        const schemasToFetch = {};
        Object.entries(dataSourceBlocks).forEach(([blockId, block]) => {
          const blockData = {};
          (block.filters || []).forEach(filter => {
            const declaredType = resolveParameterType(filter.column, filter.type);
            const storedValues = Array.isArray(filter.value) ? filter.value : [];
            blockData[filter.column] = filter.range
              ? { gte: filter.range.gte || '', lte: filter.range.lte || '' }
              : isDateRangeParam(filter.column)
                ? formatParameterInputValue(storedValues[0] || '', declaredType, { multi: false, dateRange: true })
                : storedValues;
          });
          newFormData[blockId] = {
            name: block.name || blockId,
            data: blockData,
            tableId: block.table_id
          };

          if (block.table_id) {
            schemasToFetch[block.table_id] = true;
          }
        });

        const fetchedSchemas = {};
        for (const tableId of Object.keys(schemasToFetch)) {
          try {
            const tableDoc = await getDoc(doc(db, `users/${companyName}/tables`, tableId));
            if (tableDoc.exists()) {
              fetchedSchemas[tableId] = tableDoc.data();
            }
          } catch (error) {
            console.error(`Error fetching table ${tableId}:`, error);
          }
        }
        // The selection can change during the schema reads above too.
        if (companyRef.current !== companyName || selectedFunctionRef.current !== funcId || functionSelectionGenerationRef.current !== selectionGeneration) return;
        setTableSchemas(fetchedSchemas);

        const attributeColumnsToFetch = {};
        Object.entries(dataSourceBlocks).forEach(([blockId, block]) => {
          if (block.table_id && fetchedSchemas[block.table_id]) {
            const tableSchema = fetchedSchemas[block.table_id];
            const attributeColumns = [...(tableSchema.attributeColumns || []), ...(tableSchema.tableAttributeColumns || [])];

            (block.filters || []).forEach(filter => {
              const isAttributeColumn = attributeColumns.some(mc => mc.name === filter.column);
              if (isAttributeColumn) {
                attributeColumnsToFetch[filter.column] = true;
              }
            });
          }
        });

        // This is the last guard before the parameter form is populated, so a
        // stale form can never reach the screen.
        if (companyRef.current !== companyName || selectedFunctionRef.current !== funcId || functionSelectionGenerationRef.current !== selectionGeneration) return;
        setParameterAttributeColumns(Object.keys(attributeColumnsToFetch).sort());

        setParametersFormData(newFormData);
        setExcludedParams({});
      } else {
        setParametersFormData({});
        setExcludedParams({});
      }
    } else {
      setFunctionDoc(null);
      setRunTarget(null);
      setParametersFormData({});
      setExcludedParams({});
    }
  };

  // Synchronous on purpose: `views` already holds every View document in
  // full (refreshViews maps {id, ...doc.data()}), and it is the list the user
  // clicked in. The former getDoc here could not be cancelled, so a slow read
  // for a View the user had moved off landed its cards — and its view_id on
  // every run — under the View now highlighted. handleEditView selects the
  // same way.
  const handleSelectView = (viewId) => {
    functionHistoryAbortRef.current?.abort();
    setSelectedFunctionId(null);
    selectedFunctionRef.current = null;
    setParameterAttributeColumns([]);
    setSelectedViewId(viewId);
    setViewedRunId(null);
    setViewedRunData(null);
    setFunctionRuns([]);
    setFunctionDoc(null);
    setRunTarget(null);
    setRunResult(null);
    setRunError('');

    if (!companyName || !viewId) {
      setViewData(null);
      return;
    }

    const view = views.find(v => v.id === viewId);
    setViewData(view || null);
    setViewLoadGeneration((generation) => generation + 1);
    if (!view) setSelectedViewId(null);
  };

  // Selecting an automation loads the published version it executes, because
  // every surface in its editor — parameters, watched table, email recipients —
  // is that version's contract. The ref guard is the same one function selection
  // uses: a slower load for an automation the user has moved off must never land
  // under the one now selected.
  const handleSelectAutomation = async (automation) => {
    const automationId = automation?.id || null;
    setSelectedAutomationId(automationId);
    selectedAutomationRef.current = automationId;
    setAutomationTarget(null);
    setAutomationTargetError(false);
    setRunError('');

    if (!companyName || !automation?.function_id) {
      setAutomationTargetLoading(false);
      return;
    }

    setAutomationTargetLoading(true);
    try {
      const target = await loadRunTarget(companyName, automation.function_id);
      if (selectedAutomationRef.current !== automationId) return;
      setAutomationTarget(target);
    } catch (err) {
      console.error('Failed to load the function of this automation:', err);
      if (selectedAutomationRef.current === automationId) setAutomationTargetError(true);
    } finally {
      if (selectedAutomationRef.current === automationId) setAutomationTargetLoading(false);
    }
  };

  const handleAutomationChanged = async () => {
    await refreshAutomations();
  };

  const handleAutomationDeleted = async () => {
    setSelectedAutomationId(null);
    selectedAutomationRef.current = null;
    setAutomationTarget(null);
    await refreshAutomations();
  };

  const handleAutomationCreated = async (created) => {
    await refreshAutomations();
    await handleSelectAutomation(created);
  };

  // Opening a run from the cross-function Activity feed takes the user to that
  // run's own function home, with the run already selected in the picker.
  const handleSelectActivityRun = async (run) => {
    if (!run?.function_id) return;
    setActiveTab(TAB_FUNCTIONS);
    await handleSelectFunction(run.function_id, { runId: run.id });
  };

  const handleEditFunction = async (funcId) => {
    const func = functions.find(f => f.id === funcId);
    if (!func || func.author !== 'user') {
      return;
    }

    if (selectedFunctionId !== funcId) {
      await handleSelectFunction(funcId);
    }
    openBuilder();
  };

  const handleEditView = (viewId) => {
    const view = views.find(v => v.id === viewId);
    if (!view) return;
    functionHistoryAbortRef.current?.abort();

    setSelectedViewId(viewId);
    setSelectedFunctionId(null);
    selectedFunctionRef.current = null;
    setViewData(view);
    openViewBuilder();
  };

  const handleOpenDeleteFunction = (func) => {
    if (!func || func.author !== 'user' || !companyName) return;
    setRunError('');
    functionDeletion.open(func);
  };

  const handleDeleteFunction = () => {
    // Only a user-authored function is deletable; judged on the live list, not
    // the copy the dialog was opened with.
    const func = functions.find(f => f.id === functionDeletion.target?.id);
    if (!func || func.author !== 'user') {
      functionDeletion.close();
      return;
    }
    functionDeletion.confirm();
  };

  const handleOpenDeleteView = (view) => {
    if (!view || !companyName) return;
    setRunError('');
    viewDeletion.open(view);
  };


  const handleOpenNameDialog = (func) => {
    const funcData = functions.find(f => f.id === func.id) || func;
    setFunctionToName(funcData);
    setNewFunctionName(funcData.name || '');
    setNameError('');
    setNameDialogOpen(true);
  };

  // Naming is the first step of a first publish. The name write is the only
  // step that must succeed: if it fails the dialog stays open and says so, with
  // Save as the retry. Once it has succeeded the publish dialog opens from it
  // directly — the list refresh and re-selection that follow are conveniences
  // (the function list is live anyway) and their failure must not swallow the
  // publish step the user is in the middle of.
  const handleNameSave = async () => {
    if (!companyName || !functionToName || !newFunctionName.trim() || nameSaving) return;
    const target = functionToName;

    // The Agent resolves a Function by its display name, so two Functions may
    // not share one. The backend refuses such a publish as well; checking here
    // gives the person the answer before they leave the dialog.
    const wantedName = newFunctionName.trim().toLowerCase();
    const nameTakenBy = functions.find((candidate) => (
      candidate.id !== target.id
      && typeof candidate.name === 'string'
      && candidate.name.trim().toLowerCase() === wantedName
    ));
    if (nameTakenBy) {
      setNameError('That name is already used by another Function. Choose a different name.');
      return;
    }

    setNameSaving(true);
    setNameError('');
    try {
      // A rename writes the name and nothing else. The name is display-only: it is
      // never part of a published version, so renaming neither changes what runs
      // nor touches any trigger's email permission.
      const docRef = doc(db, `users/${companyName}/data_functions`, target.id);
      await runTransaction(db, async (transaction) => {
        assertResourceAvailable(await transaction.get(docRef), 'Function');
        transaction.update(docRef, { name: newFunctionName.trim() });
      });
    } catch (err) {
      console.error('Failed to save the function name:', err);
      setNameError('Could not save the name. Try again.');
      setNameSaving(false);
      return;
    }
    setNameSaving(false);

    setNameDialogOpen(false);
    closeBuilder();
    if (!isPublishedVersion(target.published_version)) {
      setPublishingFunctionId(target.id);
    }
    setFunctionToName(null);
    setNewFunctionName('');
    setActiveTab(TAB_FUNCTIONS);

    try {
      await refreshFunctions();
      await handleSelectFunction(target.id);
    } catch (err) {
      console.error('Failed to refresh after naming the function:', err);
    }
  };

  const handleViewSaved = async (viewId, savedViewData) => {
    await refreshViews();
    functionHistoryAbortRef.current?.abort();

    setSelectedViewId(viewId);
    setSelectedFunctionId(null);
    selectedFunctionRef.current = null;
    setViewData({ id: viewId, ...savedViewData });
    setViewLoadGeneration((generation) => generation + 1);
    setActiveTab(TAB_VIEWS);
  };

  // Exclusion is per data source: the key is the block AND the column, so two
  // blocks sharing a column name can be Include in one and Exclude in the
  // other. The unified control flips every block carrying the column at once.
  const handleToggleExclude = (blockId, column) => {
    const key = `${blockId}::${column}`;
    setExcludedParams(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const isUnifiedExcluded = (param) => {
    const blockIds = Object.keys(param.valuesByBlock || {});
    return blockIds.length > 0 && blockIds.every((blockId) => excludedParams[`${blockId}::${param.name}`]);
  };
  const handleUnifiedToggleExclude = (param) => {
    const blockIds = Object.keys(param.valuesByBlock || {});
    const next = !isUnifiedExcluded(param);
    setExcludedParams(prev => {
      const copy = { ...prev };
      blockIds.forEach((blockId) => { copy[`${blockId}::${param.name}`] = next; });
      return copy;
    });
  };

  const handleUnifiedParameterChange = (column, newValue) => {
    setParametersFormData(prev => {
      const copy = { ...prev };
      Object.entries(copy).forEach(([blockId, blockObj]) => {
        if (blockObj && blockObj.data && Object.prototype.hasOwnProperty.call(blockObj.data, column)) {
          const blockFilters = functionDoc?.data_source_blocks?.[blockId]?.filters || [];
          const thisFilter = blockFilters.find(f => f.column === column);
          if (thisFilter && thisFilter.variable) {
            copy[blockId] = { ...blockObj, data: { ...blockObj.data, [column]: newValue } };
          }
        }
      });
      return copy;
    });
  };

  // Per-block editing writes to exactly one block, leaving every other block's
  // value for the same column alone.
  const handleBlockParameterChange = (blockId, column, newValue) => {
    setParametersFormData(prev => {
      const blockObj = prev[blockId];
      if (!blockObj?.data || !Object.prototype.hasOwnProperty.call(blockObj.data, column)) return prev;
      return {
        ...prev,
        [blockId]: { ...blockObj, data: { ...blockObj.data, [column]: newValue } },
      };
    });
  };

  // Clear every VARIABLE parameter across all blocks and drop any exclusions. A
  // filter the published version fixed is not editable, so it is left alone —
  // its control is disabled and parameter preparation skips it.
  const variableFilterFor = (blockId, column) => {
    const thisFilter = (functionDoc?.data_source_blocks?.[blockId]?.filters || [])
      .find((filter) => filter.column === column);
    return thisFilter?.variable ? thisFilter : null;
  };

  const hasActiveParameters = hasDateDraft
    || Object.values(excludedParams || {}).some(Boolean)
    || Object.entries(parametersFormData || {}).some(([blockId, blockObj]) => (
      Object.entries(blockObj?.data || {}).some(([column, value]) => (
        variableFilterFor(blockId, column) && !isEmptyParameterValue(value)
      ))
    ));

  const handleClearParameters = () => {
    setParametersFormData(prev => {
      const next = {};
      Object.entries(prev || {}).forEach(([blockId, blockObj]) => {
        const data = { ...(blockObj?.data || {}) };
        Object.keys(data).forEach((column) => {
          const thisFilter = variableFilterFor(blockId, column);
          if (thisFilter) data[column] = emptyParameterValue(thisFilter);
        });
        next[blockId] = { ...blockObj, data };
      });
      return next;
    });
    setExcludedParams({});
    setParameterClearGeneration((n) => n + 1);
    setDateDraftError('');
  };

  // Reload page one of this function's runs so a run that just started appears as
  // the newest entry. The displayed result is NOT repainted here — the response
  // that started the run already carries the complete composite, and the
  // generation-guarded effect replaces it once storage acknowledges a result.
  const refreshFunctionRunsList = useCallback(async (funcId, startedRunId) => {
    if (!companyName || !funcId || selectedFunctionRef.current !== funcId) return;
    functionHistoryAbortRef.current?.abort();
    const controller = new AbortController();
    functionHistoryAbortRef.current = controller;
    setFunctionRunsLoading(true);
    try {
      const { runs, cursor, hasMore } = await loadFunctionRuns(companyName, funcId, {
        signal: controller.signal,
      });
      if (controller.signal.aborted || selectedFunctionRef.current !== funcId) return;
      setFunctionRuns(runs);
      setFunctionRunsCursor(cursor);
      setFunctionRunsHasMore(hasMore);
      const started = runs.find((run) => run.id === startedRunId);
      if (started) {
        setViewedRunData(prev => (viewedRunIdRef.current === started.id ? { ...prev, ...started } : prev));
        setFunctionLastRuns(prev => ({ ...prev, [funcId]: started }));
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error('Failed to refresh the run history:', err);
      setFunctionRunsError(true);
    } finally {
      if (!controller.signal.aborted) setFunctionRunsLoading(false);
    }
  }, [companyName]);

  // Full run. The request pins the exact published version whose confirmation
  // the user reviewed; a version published after the dialog was built does not
  // change what this run executes. A 428 (effect required) re-surfaces the
  // confirmation for that same version, and a 409 means the pinned version is
  // gone, so the head is reloaded and the panel rebuilt.
  const executeRun = async (parameters, confirmed, functionVersion, functionId) => {
    if (!functionId || functionId !== selectedFunctionRef.current) return false;
    if (!isPublishedVersion(functionVersion)) {
      setRunError('Publish this function to run it.');
      return false;
    }
    // The id is read from the same run target as the version and parameters
    // (startRun), never from the selection on its own, so the three can only ever
    // describe one function. A selection that has moved on since is refused.
    const requestGeneration = ++runRequestGenerationRef.current;
    const selectionGeneration = functionSelectionGenerationRef.current;
    const requestIsCurrent = () => (
      companyRef.current === companyName
      && selectedFunctionRef.current === functionId
      && functionSelectionGenerationRef.current === selectionGeneration
      && runRequestGenerationRef.current === requestGeneration
    );
    setRunError('');
    setIsLoading(true);
    const envelope = {
      function_id: functionId,
      execution_mode: 'full',
      origin: 'manual',
      view_context: null,
      parameters,
      function_version: functionVersion,
      external_effects_confirmed: confirmed,
    };
    try {
      const result = await runFunctionEndpoint(envelope);
      // The run document itself is never synthesised here: the subscription below
      // reads it, which is also what tells us the run is not yet converged.
      // The run is this function's whatever is selected by the time it
      // answers; the PANE is the selected function's. A response arriving
      // after the user moved to another function updates nothing on screen —
      // its run is in that function's history when they come back.
      if (!requestIsCurrent()) return true;
      clearRunResultHydration(FUNCTION_RESULT_TARGET);
      setViewedRunData(null);
      viewedRunIdRef.current = result?.runId || null;
      setViewedRunId(result?.runId || null);
      setRunResult(toCompositeResult(result));
      if (result?.runId) await refreshFunctionRunsList(functionId, result.runId);
      return true;
    } catch (err) {
      if (!requestIsCurrent()) return false;
      if (err.status === 428 && !confirmed) {
        try {
          const functionName = runTarget?.head?.name || '';
          const confirmation = await buildEmailConfirmation(companyName, runTarget?.content, functionName);
          // The selection may have moved while the contacts were read: a
          // confirmation for the previous function must not open now (H47).
          if (!requestIsCurrent()) return false;
          if (confirmation) {
            setPendingEmailRun({
              functionId, functionName, parameters, confirmation, functionVersion, versionContent: runTarget?.content,
            });
            return false;
          }
        } catch (confErr) {
          console.error('Failed to build email confirmation:', confErr);
        }
      }
      if (err.status === 409 && err.code === 'function_version_unavailable') {
        try {
          await loadSelectedFunction(functionId, null, { skipHistory: true, selectionGeneration });
        } catch (refreshError) {
          if (requestIsCurrent()) setRunError(`Could not refresh this Function: ${refreshError.message}`);
          return false;
        }
        if (!requestIsCurrent()) return false;
        setRunError('This function version is no longer available. Review the refreshed parameters and run it again.');
        return false;
      }
      if (!requestIsCurrent()) return false;
      console.error(err);
      void refreshFunctionRunsList(functionId);
      setRunError(`Failed to run function: ${err.message}`);
      return false;
    } finally {
      if (requestIsCurrent()) setIsLoading(false);
    }
  };

  // Output-only retry (section 3.11). Reuses the original run's data/Logic and
  // executes only the named Output block. A 409 is feedback; a new confirmation
  // can recheck contacts that have since changed.
  const executeRetry = async (run, blockId) => {
    if (!run || !blockId) return;
    setRunError('');
    setRetryBusy((prev) => ({ ...prev, [blockId]: true }));
    const envelope = {
      function_id: run.function_id,
      execution_mode: 'outputs_only',
      run_id: run.id,
      output_block_id: blockId,
      external_effects_confirmed: true,
    };
    try {
      await runFunctionEndpoint(envelope);
      setRetryFeedback((prev) => {
        const next = { ...prev };
        delete next[blockId];
        return next;
      });
      // Reload the run doc so the subscription reactivates and the status row
      // stays live until the retry's projection catches up. The displayed result
      // is NOT optimistically merged: it changes only when the applied projection
      // generation advances.
      await refreshRun(run.id);
    } catch (err) {
      if (err.status === 409 && err.code === 'retry_already_claimed') {
        await refreshRun(run.id);
        setRetryFeedback((prev) => ({
          ...prev,
          [blockId]: 'A retry for this email is already in progress.',
        }));
      } else if (err.status === 409) {
        setRetryFeedback((prev) => ({
          ...prev,
          [blockId]: err.message || 'This output can no longer be retried.',
        }));
      } else {
        console.error(err);
        setRunError(`Failed to retry output: ${err.message}`);
      }
    } finally {
      setRetryBusy((prev) => ({ ...prev, [blockId]: false }));
    }
  };

  const refreshRun = async (runId) => {
    try {
      const snap = await getDoc(doc(db, `users/${companyName}/function_runs/${runId}`));
      if (!snap.exists()) return;
      const reloaded = { id: snap.id, ...snap.data() };
      setViewedRunData((prev) => (prev && prev.id === reloaded.id ? { ...prev, ...reloaded } : reloaded));
    } catch (err) {
      console.error('Failed to reload run:', err);
    }
  };

  // Start one run with an explicit parameter map. An effectful function requires
  // ONE confirmation, built from the exact published version this run will pin.
  const startRun = async (parameters) => {
    const functionId = runTarget?.head?.id || null;
    const functionVersion = runTarget?.publishedVersion || null;
    if (!functionId || !isPublishedVersion(functionVersion)) return;
    setRunError('');
    if (isEmailFeatureEnabled() && versionHasExternalEffect(runTarget?.content)) {
      setRunPreparing(true);
      try {
        const functionName = runTarget.head?.name || '';
        const confirmation = await buildEmailConfirmation(companyName, runTarget.content, functionName);
        // The selection may have moved while the contacts were read: a
        // confirmation for the previous function must not open now (H47).
        if (selectedFunctionRef.current !== functionId) return;
        if (confirmation) {
          setPendingEmailRun({
            functionId, functionName, parameters, confirmation, functionVersion, versionContent: runTarget.content,
          });
          return;
        }
      } catch (err) {
        // Fall through to a normal run; the backend 428 re-surfaces confirmation.
        console.error('Failed to prepare email confirmation:', err);
      } finally {
        setRunPreparing(false);
      }
    }
    await executeRun(parameters, false, functionVersion, functionId);
  };

  // A start after its end in any block's dates refuses the run (M41).
  const invertedFormDates = (formData) => (
    Object.values(formData || {}).map((block) => findInvertedDateRange(block?.data)).find(Boolean) || null
  );

  const handleRunFunction = async () => {
    if (!selectedFunctionId || !functionDoc || functionDoc.invalid) return;
    if (dateScopeRef.current?.reveal()) {
      setDateDraftError(INCOMPLETE_DATES_MESSAGE);
      return;
    }
    const invertedDates = invertedFormDates(parametersFormData);
    if (invertedDates) {
      setDateDraftError(invertedDates);
      return;
    }
    setDateDraftError('');
    if (preparedParameters.errors.length > 0) {
      setRunError(formatParameterErrors(preparedParameters.errors, functionDoc.data_source_blocks));
      return;
    }
    await startRun(preparedParameters.parameters);
  };

  // Reuse historical values only after checking their original contract.
  // The run still pins the version currently shown in this Function pane.
  const handleRerunParameters = async () => {
    if (!selectedFunctionId || !functionDoc || functionDoc.invalid || !viewedRunData || runPreparing) return;
    if (dateScopeRef.current?.reveal()) {
      setDateDraftError(INCOMPLETE_DATES_MESSAGE);
      return;
    }
    const target = runTarget;
    const functionId = selectedFunctionId;
    setRunPreparing(true);
    setRunError('');
    try {
      const prepared = await prepareHistoricalRunParameters(companyName, viewedRunData, {
        functionId, version: target.publishedVersion, content: target.content,
      });
      if (selectedFunctionRef.current !== functionId || runTargetRef.current !== target) return;
      if (prepared.errors.length > 0) {
        setRunError(`${formatParameterErrors(prepared.errors, functionDoc.data_source_blocks)}. Review the current parameters and start a new run.`);
        return;
      }
      const controls = controlsFromRunParameters(functionDoc.data_source_blocks, prepared.parameters);
      const formData = {};
      Object.entries(parametersFormData).forEach(([blockId, block]) => {
        const fixedValues = Object.fromEntries(Object.entries(block.data || {}).filter(([column]) => (
          (functionDoc.data_source_blocks[blockId]?.filters || []).some((filter) => filter.column === column && !filter.variable)
        )));
        formData[blockId] = { ...block, data: { ...fixedValues, ...controls.functionParams[blockId]?.data } };
      });
      setParametersFormData(formData);
      setExcludedParams(controls.excludedColumns);
      setDateDraftError('');
      await startRun(prepared.parameters);
    } catch (error) {
      if (selectedFunctionRef.current === functionId && runTargetRef.current === target) {
        setRunError(`Could not reuse these parameters: ${error.message}`);
      }
    } finally {
      setRunPreparing(false);
    }
  };

  // `Retry original email` resends one historical email. Its confirmation is
  // built entirely from the authoritative Email ledger reached through this
  // Output's `email_id`. Current contacts control availability only; they cannot
  // change the historical addresses. Mismatched ledger state fails closed.
  const handleRetryOutput = async (run, blockId) => {
    if (!run || !blockId) return;
    setRunError('');
    setRetryRequest({ run, blockId, detail: null, error: '' });
    setRetryBusy((prev) => ({ ...prev, [blockId]: true }));
    try {
      const summary = runOutputsSummary(viewedRunData)[blockId] || {};
      const detail = await loadEmailRetryDetail(companyName, summary.email_id, {
        functionId: run.function_id,
        runId: run.id,
        outputBlockId: blockId,
      });
      setRetryRequest((prev) => (
        prev && prev.blockId === blockId ? { ...prev, detail } : prev
      ));
    } catch (err) {
      console.error('Failed to load the original email:', err);
      setRetryRequest((prev) => (
        prev && prev.blockId === blockId ? { ...prev, error: err.message } : prev
      ));
    } finally {
      setRetryBusy((prev) => ({ ...prev, [blockId]: false }));
    }
  };

  const handleConfirmRetry = async () => {
    if (!retryRequest?.detail || runConfirmBusy) return;
    const { run, blockId } = retryRequest;
    setRunConfirmBusy(true);
    try {
      await executeRetry(run, blockId);
    } finally {
      setRunConfirmBusy(false);
      setRetryRequest(null);
    }
  };

  const handleCancelRetry = () => {
    if (runConfirmBusy) return;
    setRetryRequest(null);
  };

  const handleConfirmEmailRun = async () => {
    if (!pendingEmailRun || runConfirmBusy) return;
    const request = pendingEmailRun;
    setRunConfirmBusy(true);
    let keepOpen = false;
    try {
      // The contact book is read again at the moment of consent, for the same
      // version the dialog was built from. A recipient whose address, name,
      // or eligibility changed since is shown again rather than run; a read
      // that fails runs nothing (H38).
      let fresh;
      try {
        fresh = await buildEmailConfirmation(companyName, request.versionContent, request.functionName);
      } catch (err) {
        console.error('Failed to re-check the email recipients:', err);
        keepOpen = true;
        setPendingEmailRun({ ...request, error: 'The recipients could not be re-checked. Try again.' });
        return;
      }
      if (fresh && confirmationChanged(request.confirmation, fresh)) {
        keepOpen = true;
        setPendingEmailRun({ ...request, confirmation: fresh, changed: true, error: '' });
        return;
      }
      if (fresh && blockedRecipients(fresh).length > 0) {
        keepOpen = true;
        setPendingEmailRun({ ...request, confirmation: fresh, error: '' });
        return;
      }
      await executeRun(request.parameters, true, request.functionVersion, request.functionId);
    } finally {
      setRunConfirmBusy(false);
      if (!keepOpen) setPendingEmailRun(null);
    }
  };

  // A declined confirmation cancels only that run.
  const handleCancelEmailRun = () => {
    if (runConfirmBusy) return;
    setPendingEmailRun(null);
  };

  useEffect(() => {
    viewedRunIdRef.current = viewedRunId;
  }, [viewedRunId]);

  const runResultHydration = (
    runResultHydrations[FUNCTION_RESULT_TARGET]?.runId === viewedRunId
      ? runResultHydrations[FUNCTION_RESULT_TARGET]
      : null
  );
  const resultFetchInFlight = runResultHydration?.status === 'loading';
  const resultHydrationError = runResultHydration?.status === 'error'
    ? runResultHydration.error
    : '';

  // Terminal compute status is NOT enough to stop watching: Email delivery is
  // reconciled afterwards and projected into the result. The subscription stays
  // open while the lifecycle is active, the outcome is pending, any Email
  // Output's applied projection is behind its desired one, or an acknowledged
  // generation fetch is still in flight.
  const displayedRunSettled = (
    viewedRunData?.id === viewedRunId
    && isRunSettled(viewedRunData, resultFetchInFlight)
  );

  useEffect(() => {
    if (!companyName || !viewedRunId || displayedRunSettled) return undefined;
    const runRef = doc(db, `users/${companyName}/function_runs/${viewedRunId}`);
    const unsubscribe = onSnapshot(runRef, (snap) => {
      if (!snap.exists()) return;
      const next = { id: snap.id, ...snap.data() };
      setViewedRunData((prev) => (prev && prev.id === next.id ? { ...prev, ...next } : next));
      // A run that finishes while it is on screen is also this function's newest
      // run, so the sidebar dot follows it without a second query.
      setFunctionLastRuns((prev) => {
        const current = prev[next.function_id];
        if (current && current.id !== next.id) return prev;
        return { ...prev, [next.function_id]: next };
      });
    }, (err) => {
      console.error('Failed to subscribe to the run:', err);
    });
    return () => unsubscribe();
  }, [companyName, viewedRunId, displayedRunSettled]);

  // Refresh the displayed result ONLY when the run acknowledges a newer result
  // generation. Every other run-document change (stage status, summary edits)
  // updates the live state above without re-downloading result.json.
  useEffect(() => {
    const run = viewedRunData;
    if (!run || run.id !== viewedRunId || !run.result_gcs_path) return;
    hydrateRunResult(FUNCTION_RESULT_TARGET, run);
  }, [viewedRunData, viewedRunId, hydrateRunResult]);

  const handleRetryRunResult = useCallback(() => {
    if (!viewedRunData || viewedRunData.id !== viewedRunId) return;
    retryRunResultHydration(FUNCTION_RESULT_TARGET, viewedRunData);
  }, [viewedRunData, viewedRunId, retryRunResultHydration]);


  // One control per column across the blocks — only where the blocks agree on
  // what that column is (type, range-or-value, default: `filterSignature`) and
  // every block's filter is variable. A column the blocks disagree on cannot
  // be driven by one control (a range written into a plain filter is rejected
  // at run time), so it is listed in `unmergeableColumns` and shown per block.
  const unmergeableColumns = useMemo(() => {
    const signatures = {};
    Object.entries(parametersFormData || {}).forEach(([blockId, blockObj]) => {
      const blockFilters = functionDoc?.data_source_blocks?.[blockId]?.filters || [];
      Object.keys(blockObj.data || {}).forEach((col) => {
        const thisFilter = blockFilters.find(f => f.column === col);
        if (!signatures[col]) signatures[col] = new Set();
        signatures[col].add(thisFilter?.variable ? filterSignature(thisFilter) : 'fixed');
      });
    });
    return new Set(Object.entries(signatures).filter(([, set]) => set.size > 1).map(([col]) => col));
  }, [parametersFormData, functionDoc]);

  // The unified columns whose blocks currently hold different values or IS/NOT
  // bits. One control would show blank while a run sent every block its own
  // value, so these are listed per source as well, until the blocks agree
  // (M218).
  const differingColumns = useMemo(() => new Set(
    hiddenDisagreements(parametersFormData, excludedParams, functionDoc?.data_source_blocks),
  ), [parametersFormData, excludedParams, functionDoc]);

  const unifiedParameters = useMemo(() => {
    const paramsMap = {};
    Object.entries(parametersFormData || {}).forEach(([blockId, blockObj]) => {
      const blockFilters = functionDoc?.data_source_blocks?.[blockId]?.filters || [];
      Object.keys(blockObj.data || {}).forEach((col) => {
        if (unmergeableColumns.has(col) || differingColumns.has(col)) return;
        if (!paramsMap[col]) {
          paramsMap[col] = {
            name: col,
            valuesByBlock: {},
            anyVariable: false,
            isRange: false,
            tableIds: new Set(),
            types: new Set(),
          };
        }
        paramsMap[col].valuesByBlock[blockId] = blockObj.data[col] ?? '';
        if (blockObj?.tableId) {
          paramsMap[col].tableIds.add(blockObj.tableId);
        }
        const thisFilter = blockFilters.find(f => f.column === col);
        if (thisFilter && thisFilter.variable) {
          paramsMap[col].anyVariable = true;
        }
        if (thisFilter?.range) {
          paramsMap[col].isRange = true;
        }
        paramsMap[col].types.add(resolveParameterType(col, thisFilter?.type));
      });
    });

    const isAttributeColumnByName = {};
    Object.values(tableSchemas || {}).forEach(schema => {
      const metadataCols = [...(schema?.attributeColumns || []), ...(schema?.tableAttributeColumns || [])];
      metadataCols.forEach(mc => {
        isAttributeColumnByName[mc.name] = true;
      });
    });

    return Object.values(paramsMap)
      .map(p => ({
        ...p,
        tableIds: Array.from(p.tableIds || []),
        isAttribute: !!isAttributeColumnByName[p.name],
        type: Array.from(p.types || [resolveParameterType(p.name, 'Text')])[0] || 'Text',
        isDateRange: isDateRangeParam(p.name),
      }))
      .sort(compareParameters);
  }, [parametersFormData, functionDoc, tableSchemas, unmergeableColumns, differingColumns]);

  // The same parameters, grouped by the data-source block that declares them.
  // Unified editing writes one value to every block carrying that column; this
  // grouping is the escape hatch for a function whose two sources genuinely want
  // different values for the same column name.
  const blockParameters = useMemo(() => {
    const isAttributeColumnByName = {};
    Object.values(tableSchemas || {}).forEach(schema => {
      [...(schema?.attributeColumns || []), ...(schema?.tableAttributeColumns || [])]
        .forEach(column => { isAttributeColumnByName[column.name] = true; });
    });

    return Object.entries(parametersFormData || {})
      .map(([blockId, blockObj]) => {
        const blockFilters = functionDoc?.data_source_blocks?.[blockId]?.filters || [];
        const params = Object.keys(blockObj.data || {})
          .map((col) => {
            const thisFilter = blockFilters.find(f => f.column === col);
            return {
              name: col,
              value: blockObj.data[col],
              variable: Boolean(thisFilter?.variable),
              isRange: Boolean(thisFilter?.range),
              isDateRange: isDateRangeParam(col),
              isAttribute: Boolean(isAttributeColumnByName[col]),
              type: resolveParameterType(col, thisFilter?.type),
              tableIds: blockObj.tableId ? [blockObj.tableId] : [],
            };
          })
          .sort(compareParameters);
        return { blockId, name: blockObj.name || blockId, params };
      })
      .filter(group => group.params.length > 0);
  }, [parametersFormData, functionDoc, tableSchemas]);

  // The per-block controls for the columns the unified list cannot carry:
  // those the blocks define differently, and those they currently hold
  // differently.
  const groupsForColumns = useCallback((columns) => (
    columns.size === 0
      ? []
      : blockParameters
        .map((group) => ({ ...group, params: group.params.filter((param) => columns.has(param.name)) }))
        .filter((group) => group.params.length > 0)
  ), [blockParameters]);
  const separateBlockParameters = useMemo(
    () => groupsForColumns(unmergeableColumns),
    [groupsForColumns, unmergeableColumns],
  );
  const differingBlockParameters = useMemo(
    () => groupsForColumns(differingColumns),
    [groupsForColumns, differingColumns],
  );

  // A text column is searched on the server only when the table actually indexes
  // it — and never for an external table, which Decode reads through rather than
  // stores and therefore cannot index.
  const indexedTableIdsFor = useCallback((name, type, tableIds) => (
    (tableIds || []).filter((tableId) => (
      tableId
      && type === 'Text'
      && !isExternalTable(tableSchemas?.[tableId])
      && tableSchemas?.[tableId]?.detailColumns?.some((column) => column?.name === name && column?.index)
    ))
  ), [tableSchemas]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Clear all selections when switching tabs
    if (newValue !== TAB_FUNCTIONS) {
      functionHistoryAbortRef.current?.abort();
      setFunctionRunsLoading(false);
      setSelectedFunctionId(null);
      selectedFunctionRef.current = null;
      setFunctionDoc(null);
      setRunTarget(null);
      setRunResult(null);
      setViewedRunId(null);
      setViewedRunData(null);
      setFunctionRuns([]);
      setRunMenuAnchor(null);
      setParametersFormData({});
      setExcludedParams({});
    }
    if (newValue !== TAB_VIEWS) {
      setSelectedViewId(null);
      setViewData(null);
    }
    if (newValue !== TAB_AUTOMATIONS) {
      setSelectedAutomationId(null);
      selectedAutomationRef.current = null;
      setAutomationTarget(null);
    }
    // Reset common display state
    setRunError('');
  };

  const toggleDraftsExpansion = () => {
    setDraftsExpanded(!draftsExpanded);
  };

  // Automations are listed across the whole company, so a row has to name the
  // function it runs and the table it watches itself.
  const tableNameById = useMemo(() => tables.reduce((names, table) => (
    table?.id ? { ...names, [table.id]: formatText(table.name || table.id) } : names
  ), {}), [tables]);

  const functionNameById = useMemo(() => functions.reduce((names, func) => ({
    ...names,
    [func.id]: func.name || func.id,
  }), {}), [functions]);

  // A `disabled_reason` written by the backend names the stable table ID,
  // because an ID survives a rename and a name does not. Display resolves it.
  const resolveTableIds = useCallback((text) => (
    Object.entries(tableNameById).reduce((resolved, [tableId, name]) => (
      resolved.includes(tableId) ? resolved.split(tableId).join(name) : resolved
    ), String(text || ''))
  ), [tableNameById]);

  // Tab labels come from the version the DISPLAYED run pinned, so a block renamed
  // since does not relabel old results. A run of the version published now reads
  // the target already loaded; an older run's version is read (and cached) on
  // demand. Until it arrives, or if it is gone, the tabs fall back to their entry
  // keys — never to the current names.
  // Names of older versions, keyed `${functionId}:${version}` (null = gone); a
  // version is immutable, so entries are never dropped.
  const [historicalBlockNames, setHistoricalBlockNames] = useState({});
  const viewedRunVersion = viewedRunData?.function_version ?? null;
  const viewedRunIsCurrent = viewedRunVersion === null || viewedRunVersion === runTarget?.publishedVersion;
  useEffect(() => {
    if (!companyName || !selectedFunctionId || viewedRunIsCurrent) return;
    const key = `${selectedFunctionId}:${viewedRunVersion}`;
    if (key in historicalBlockNames) return;
    loadVersionBlockNames(companyName, selectedFunctionId, viewedRunVersion)
      .then((names) => setHistoricalBlockNames((prev) => (key in prev ? prev : { ...prev, [key]: names })))
      .catch((err) => console.error('Failed to read the block names of this run\'s version:', err));
  }, [companyName, selectedFunctionId, viewedRunVersion, viewedRunIsCurrent, historicalBlockNames]);
  const runBlockNames = useMemo(() => {
    if (!runTarget) return null;
    if (viewedRunIsCurrent) return versionBlockNames(runTarget.content);
    return historicalBlockNames[`${selectedFunctionId}:${viewedRunVersion}`] ?? null;
  }, [runTarget, viewedRunIsCurrent, historicalBlockNames, selectedFunctionId, viewedRunVersion]);

  const renderFunctionResult = (context = {}) => {
    if (!runResult) return null;

    // The Excel export's header rows describe the parameters the DISPLAYED run
    // executed with, read from its run document — never the current controls,
    // which are editable without re-running and, for a run picked from history,
    // still show whatever the user last typed. The controls are consulted only
    // while a just-started run's document has not yet been loaded, when they are
    // exactly what ran.
    const exportHeaders = (() => {
      const out = {};

      const storedParameters = viewedRunData && viewedRunData.id === viewedRunId && isPlainObject(viewedRunData.parameters)
        ? viewedRunData.parameters
        : null;
      if (storedParameters) {
        // Same shape a run request sends: { [blockId]: { column: values,
        // exclude_filters: {column: values}, range_filters: {column: {gte, lte}} } }.
        // First block with a value for a column wins, as with the controls.
        const put = (column, display) => {
          if (isDateRangeParam(column) || display === '' || out[column] !== undefined) return;
          out[column] = display;
        };
        Object.values(storedParameters).forEach((blockValues) => {
          if (!isPlainObject(blockValues)) return;
          Object.entries(blockValues).forEach(([key, value]) => {
            if (key === 'range_filters') {
              if (!isPlainObject(value)) return;
              Object.entries(value).forEach(([column, range]) => {
                const cleaned = cleanRangeValue(range);
                if (Object.keys(cleaned).length > 0) put(column, `${cleaned.gte || '…'} → ${cleaned.lte || '…'}`);
              });
              return;
            }
            if (key === 'exclude_filters') {
              if (!isPlainObject(value)) return;
              Object.entries(value).forEach(([column, excludedValues]) => {
                const list = Array.isArray(excludedValues) ? excludedValues : [excludedValues];
                if (list.length > 0) put(column, `Excluding: ${list.join(', ')}`);
              });
              return;
            }
            if (Array.isArray(value)) put(key, value.join(', '));
            else if (typeof value === 'string') put(key, value.trim());
          });
        });
        return out;
      }

      (unifiedParameters || []).forEach(param => {
        if (isDateRangeParam(param.name)) return;
        const values = Object.values(param.valuesByBlock || {});
        const firstNonEmpty = values.find((value) => {
          if (param.isRange) return Object.keys(cleanRangeValue(value)).length > 0;
          return Array.isArray(value) ? value.length > 0 : (typeof value === 'string' ? value.trim() !== '' : false);
        });
        let display = '';
        if (param.isRange && firstNonEmpty) {
          const range = cleanRangeValue(firstNonEmpty);
          display = `${range.gte || '…'} → ${range.lte || '…'}`;
        } else if (Array.isArray(firstNonEmpty)) display = firstNonEmpty.join(', ');
        else if (typeof firstNonEmpty === 'string') display = firstNonEmpty;
        if (display !== '') out[param.name] = display;
      });
      return out;
    })();

    const runId = context.runId || null;
    const functionId = context.functionId || viewedRunData?.function_id || selectedFunctionId;
    // The run document's summary is authoritative for what exists and what state
    // each Output is in; result.json only carries acknowledged payloads.
    const summaryMap = context.summaryMap || null;
    const emailOutputs = isEmailFeatureEnabled()
      ? collectEmailOutputs(runResult.outputs, summaryMap)
      : [];
    const retryTarget = runId ? { id: runId, function_id: functionId } : null;

    return (
      <>
        {emailOutputs.length > 0 && retryTarget && (
          <EmailOutputsPanel
            emailOutputs={emailOutputs}
            onRetry={(blockId) => handleRetryOutput(retryTarget, blockId)}
            retryBusy={retryBusy}
            retryFeedback={retryFeedback}
          />
        )}
        <CompositeOutputTabs
          outputs={runResult.outputs}
          outputsSummary={summaryMap}
          functionId={functionId}
          companyName={companyName}
          chartSettings={selectedChartSettings}
          exportName={exportFileName(functionDoc?.name, runExportStamp(viewedRunData))}
          exportHeaders={exportHeaders}
          blockNames={runBlockNames}
        />
      </>
    );
  };

  // Runs execute published versions only, so an unpublished function cannot run
  // at all and an invalid one references a dependency that no longer exists.
  // One data source's parameter controls; used by the per-block list and, in
  // unified mode, for the columns that could not be merged.
  const renderBlockGroup = (group) => (
                      <Box key={group.blockId} sx={{ mb: 2.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: 'text.secondary',
                          }}
                        >
                          {formatText(group.name)}
                        </Typography>
                        <Divider sx={{ mb: 1.5, mt: 0.5 }} />
                        {group.params.map((param) => (
                          <ParameterField
                            key={param.name}
                            name={param.name}
                            error={preparedParameters.errors.find((issue) => (
                              issue.blockId === group.blockId && issue.column === param.name
                            ))?.message || ''}
                            value={param.value}
                            onChange={(next) => handleBlockParameterChange(group.blockId, param.name, next)}
                            type={param.type}
                            isRange={param.isRange}
                            isDateRange={param.isDateRange}
                            isAttribute={param.isAttribute}
                            availableValues={attributeValues[param.name]}
                            indexedTableIds={indexedTableIdsFor(param.name, param.type, param.tableIds)}
                            disabled={!param.variable}
                            excluded={Boolean(excludedParams[`${group.blockId}::${param.name}`])}
                            onToggleExclude={() => handleToggleExclude(group.blockId, param.name)}
                          />
                        ))}
                      </Box>
  );

  const runnableVersion = runTarget?.publishedVersion || null;
  const selectedDeletingFunction = functions.find((f) => f.id === selectedFunctionId && isResourceDeleting(f));
  let runDisabledReason = '';
  if (selectedDeletingFunction) {
    runDisabledReason = 'This Function is being deleted.';
  } else if (!isPublishedVersion(runnableVersion)) {
    runDisabledReason = 'Publish this function to run it';
  } else if (!runTarget?.content) {
    runDisabledReason = 'Published Function content is unavailable.';
  } else if (functionDoc?.invalid) {
    runDisabledReason = functionDoc.invalid_reason || 'This function references a deleted resource.';
  }
  const resultLoading = isLoading || resultFetchInFlight;

  // The live function list notices a publish of the selected function made
  // elsewhere. The pane keeps running the version it loaded — its parameter
  // form belongs to that version — and offers a reload rather than resetting a
  // form the user may be editing.
  const liveSelectedHead = functions.find((f) => f.id === selectedFunctionId) || null;
  const liveSelectedVersion = isPublishedVersion(liveSelectedHead?.published_version)
    ? liveSelectedHead.published_version
    : null;
  const paneRepublished = Boolean(
    runTarget && liveSelectedHead && liveSelectedVersion !== runTarget.publishedVersion
  );
  // The dirty flag is read live for the same reason: a chart-settings save, or
  // any draft edit made elsewhere, marks the function the moment it lands
  // rather than on the next selection. The one-shot target only fills in until
  // the live list has the head.
  const selectedHasUnpublishedChanges = liveSelectedHead
    ? liveSelectedHead.has_unpublished_changes === true
    : Boolean(runTarget?.hasUnpublishedChanges);
  // The function's chart display settings, live from the same list, applied to
  // whichever run is on screen.
  const selectedChartSettings = liveSelectedHead
    ? (liveSelectedHead.chart_settings ?? null)
    : (runTarget?.head?.chart_settings ?? null);

  // The function home occupies the centre pane and pushes the parameter rail into
  // view, so the layout width calculation, the pane itself and the rail must all
  // agree on one condition — including the tab, so a function selection left over
  // from a previous tab never widens the Views or Activity pane.
  const functionPaneOpen = activeTab === TAB_FUNCTIONS && Boolean(selectedFunctionId);
  const selectedAutomation = automations.find((automation) => automation.id === selectedAutomationId) || null;
  const automationPaneOpen = activeTab === TAB_AUTOMATIONS && Boolean(selectedAutomation);
  // What the sidebar's primary action creates depends on the tab it is under.
  const newResourceAction = {
    [TAB_VIEWS]: { label: 'New View', onClick: handleCreateView },
    [TAB_FUNCTIONS]: { label: 'New Function', onClick: handleCreateFunction, disabled: creatingFunction, error: createFunctionError },
    [TAB_AUTOMATIONS]: { label: 'New Automation', onClick: () => setShowCreateAutomation(true) },
  }[activeTab] || null;
  const [paramsCollapsed, setParamsCollapsed] = useState(false);
  // Right rail width; collapses to a slim 56px strip. (The left rail is the
  // shared LeftSidebar, which sizes itself.)
  const paramsWidth = { md: paramsCollapsed ? '56px' : '25%', lg: paramsCollapsed ? '56px' : '280px' };

  const viewedRunOutcome = viewedRunData ? deriveRunOutcome(viewedRunData) : null;
  const viewedRunFailed = viewedRunData ? runFailed(viewedRunData) : false;
  // A run executes the version it pinned. When that is not the version the
  // function currently publishes, say so — the result on screen was produced by
  // different logic from the one the Run button would execute now.
  const viewedVersionMismatch = Boolean(
    viewedRunData
    && isPublishedVersion(runnableVersion)
    && Number.isInteger(viewedRunData.function_version)
    && viewedRunData.function_version !== runnableVersion
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          height: '100%',
          flexGrow: 1,
          m: 0,
          width: '100%',
          display: 'flex',
        }}
      >
        {/* Left Sidebar - Flush with left edge, no rounded corners */}
        <LeftSidebar>
          <Box
            sx={{
              p: 2,
              pt: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Tabs for toggling between views, functions, automations and the
                activity feed. Views comes first and is the landing tab: a
                dashboard is what a user opens the page to look at, not the
                machinery behind it. */}
            {/* Each tab stacks a small label under its icon when the rail is
                wide enough for all four labels, and falls back to icon-only
                (name on hover and on the accessible label) when it is not —
                showTabLabels tracks the strip's width live via
                attachTabsNode. minWidth is removed either way: MUI's 90px
                Tab minimum would overflow this rail. */}
            <Tabs
              ref={attachTabsNode}
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                mb: 2,
                '& .MuiTab-root': {
                  minWidth: 0,
                  px: 0.5,
                  ...(showTabLabels ? {
                    minHeight: 56,
                    fontSize: '0.7rem',
                    textTransform: 'none',
                  } : {}),
                },
                ...(showTabLabels ? {
                  '& .MuiTab-iconWrapper': {
                    mb: 0.25,
                  },
                } : {}),
              }}
            >
              {[
                { name: 'Views', icon: <DashboardIcon />, key: 'views' },
                { name: 'Functions', icon: <FunctionsIcon />, key: 'functions' },
                { name: 'Automations', icon: <BoltIcon />, key: 'automations' },
                { name: 'Activity', icon: <HistoryIcon />, key: 'activity' },
              ].map(({ name, icon, key }) => (
                <Tab
                  key={key}
                  icon={showTabLabels ? icon : <Tooltip title={name}>{icon}</Tooltip>}
                  label={showTabLabels ? name : undefined}
                  aria-label={showTabLabels ? undefined : name}
                  id={`${key}-tab`}
                  aria-controls={`${key}-panel`}
                />
              ))}
            </Tabs>

            {/* Functions Tab Panel */}
            <Box
              role="tabpanel"
              hidden={activeTab !== TAB_FUNCTIONS}
              id="functions-panel"
              aria-labelledby="functions-tab"
              sx={{ display: activeTab === TAB_FUNCTIONS ? 'flex' : 'none', flexDirection: 'column', flex: 1, overflow: 'auto' }}
            >
              {functions.filter(isResourceDeleting).map((resource) => (
                <ResourceDeletionNotice key={resource.id} resource={resource} onRetry={() => handleOpenDeleteFunction(resource)} />
              ))}
              {/* Combined functions list (no separation between custom and predefined) */}
              <List>
                {finalFunctions.map(f => {
                  const lastRun = functionLastRuns[f.id];
                  const lastOutcome = lastRun ? deriveRunOutcome(lastRun) : null;
                  const dotColor = lastOutcome ? outcomeDotColor(lastOutcome) : null;
                  return (
                    <ListItem
                      key={f.id}
                      selected={f.id === selectedFunctionId}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start'
                      }}
                    >
                      <Box sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        {dotColor && (
                          <Tooltip title={`Last run: ${outcomeDisplay(lastOutcome).label}`}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: dotColor,
                                flexShrink: 0,
                                mr: 1,
                              }}
                            />
                          </Tooltip>
                        )}
                        {/* A name long enough to wrap turns the row into a
                            word-per-line column, so it is clipped to one line
                            and the full name is available on hover. */}
                        <ListItemText
                          primary={f.name || f.id}
                          primaryTypographyProps={{ noWrap: true, title: f.name || f.id }}
                          secondary={lastRun ? formatRunTimeShort(lastRun.created_at) : null}
                          secondaryTypographyProps={{ variant: 'caption', color: 'text.disabled', noWrap: true }}
                          onClick={() => handleSelectFunction(f.id)}
                          sx={{ cursor: 'pointer', flex: '1 1 auto', minWidth: 0, my: 0 }}
                        />
                        <Box sx={{
                          display: 'flex',
                          flexShrink: 0,
                          ml: 1,
                          gap: 0.25,
                          alignItems: 'center'
                        }}>
                          {f.invalid && (
                            <Tooltip title={f.invalid_reason || 'Function is invalid'}>
                              <Chip
                                label="Invalid"
                                color="warning"
                                size="small"
                                sx={{ mr: 0.5, height: 22 }}
                              />
                            </Tooltip>
                          )}
                          {isPublishedVersion(f.published_version) && f.has_unpublished_changes === true && (
                            <Tooltip title={`Unpublished changes — runs, Automations and emails use version ${f.published_version} until you publish again`}>
                              <Chip
                                label="Unpublished edits"
                                color="warning"
                                variant="outlined"
                                size="small"
                                sx={{ mr: 0.5, height: 22 }}
                              />
                            </Tooltip>
                          )}
                          {isFunctionRunnable(f) && (
                            <Tooltip title={`Published — runs version ${f.published_version}`}>
                              <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 0.5 }}/>
                            </Tooltip>
                          )}
                          {!isPublishedVersion(f.published_version) && (
                            <Tooltip title="Not published — publish this function to run it">
                              <WarningIcon color="warning" fontSize="small" sx={{ mr: 0.5 }}/>
                            </Tooltip>
                          )}
                          <IconButton size="small" onClick={() => handleEditFunction(f.id)} disabled={f.author !== 'user'}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleOpenDeleteFunction(f)} disabled={f.author !== 'user'}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                })}

                {resourceErrors.functions && <LoadFailure thing="functions" onRetry={retryResources} />}
                {finalFunctions.length === 0 && !resourceErrors.functions && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No functions created yet.
                  </Typography>
                )}
              </List>
            </Box>

            {/* Views Tab Panel */}
            <Box
              role="tabpanel"
              hidden={activeTab !== TAB_VIEWS}
              id="views-panel"
              aria-labelledby="views-tab"
              sx={{ display: activeTab === TAB_VIEWS ? 'flex' : 'none', flexDirection: 'column', flex: 1 }}
            >
              <List>
                {views.filter(isResourceDeleting).map((resource) => (
                  <ResourceDeletionNotice key={resource.id} resource={resource} onRetry={() => viewDeletion.open(resource)} />
                ))}
                {views.filter((view) => !isResourceDeleting(view)).map(view => (
                  <ListItem
                    key={view.id}
                    selected={view.id === selectedViewId}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start'
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <ListItemText
                        primary={view.name || view.id}
                        primaryTypographyProps={{ noWrap: true, title: view.name || view.id }}
                        onClick={() => handleSelectView(view.id)}
                        sx={{ cursor: 'pointer', flex: '1 1 auto', minWidth: 0, my: 0 }}
                      />
                      <Box sx={{
                        display: 'flex',
                        flexShrink: 0,
                        ml: 1,
                        gap: 0.25,
                        alignItems: 'center'
                      }}>
                        <IconButton size="small" onClick={() => handleEditView(view.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenDeleteView(view)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItem>
                ))}

                {resourceErrors.views && <LoadFailure thing="views" onRetry={retryResources} />}
                {views.length === 0 && !resourceErrors.views && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No views created yet.
                  </Typography>
                )}
              </List>
            </Box>

            {/* Automations Tab Panel — every automation in the company, whichever
                function it runs, because "what is running unattended?" is a
                question about the company and not about one function. */}
            <Box
              role="tabpanel"
              hidden={activeTab !== TAB_AUTOMATIONS}
              id="automations-panel"
              aria-labelledby="automations-tab"
              sx={{ display: activeTab === TAB_AUTOMATIONS ? 'flex' : 'none', flexDirection: 'column', flex: 1, overflow: 'auto' }}
            >
              <List>
                {automations.map((automation) => (
                  <ListItem
                    key={automation.id}
                    selected={automation.id === selectedAutomationId}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start'
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <ListItemText
                        primary={automation.name || automation.id}
                        primaryTypographyProps={{ noWrap: true, title: automation.name || automation.id }}
                        secondary={`${formatText(functionNameById[automation.function_id] || automation.function_name || automation.function_id)} · ${describeWhen(automation, tableNameById)}`}
                        secondaryTypographyProps={{ variant: 'caption', color: 'text.disabled', noWrap: true }}
                        onClick={() => handleSelectAutomation(automation)}
                        sx={{ cursor: 'pointer', flex: '1 1 auto', minWidth: 0, my: 0 }}
                      />
                      <Box sx={{
                        display: 'flex',
                        flexShrink: 0,
                        ml: 1,
                        gap: 0.25,
                        alignItems: 'center'
                      }}>
                        {automation.enabled !== true && (
                          <Tooltip title={resolveTableIds(automation.disabled_reason) || 'Switched off — nothing runs until it is switched on'}>
                            <Chip label="Off" size="small" sx={{ height: 22 }} />
                          </Tooltip>
                        )}
                        {automation.allow_email === true && (
                          <Tooltip title="Allowed to send this function's Email Outputs on every run">
                            <Chip label="Email" color="warning" size="small" sx={{ height: 22 }} />
                          </Tooltip>
                        )}
                        {automation.managed_by && (
                          <Tooltip title="Created and maintained by a View's Keep current dialog">
                            <Chip label="View-managed" size="small" variant="outlined" sx={{ height: 22 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                ))}

                {resourceErrors.automations && <LoadFailure thing="automations" onRetry={retryResources} />}
                {automations.length === 0 && !resourceErrors.automations && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No automations yet.
                  </Typography>
                )}
              </List>
            </Box>

            {/* Activity Tab Panel — the feed itself lives in the centre pane, which
                has the room for its filters; the sidebar just explains it. */}
            <Box
              role="tabpanel"
              hidden={activeTab !== TAB_ACTIVITY}
              id="activity-panel"
              aria-labelledby="activity-tab"
              sx={{ display: activeTab === TAB_ACTIVITY ? 'flex' : 'none', flexDirection: 'column', flex: 1 }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, lineHeight: 1.7 }}>
                Every run of every function, newest first. Click a row to open that run
                in its function&apos;s home.
              </Typography>
            </Box>

            {/* Spacer to push the footer to the bottom */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Drafts section (expandable) — Functions tab only */}
            {activeTab === TAB_FUNCTIONS && (
              <Box sx={{ mb: 2 }}>
                <Button
                  onClick={toggleDraftsExpansion}
                  fullWidth
                  endIcon={draftsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    justifyContent: 'space-between',
                    color: 'text.secondary',
                    mb: 1
                  }}
                >
                  Drafts ({draftFunctions.length})
                </Button>

                {draftsExpanded && (
                  <List dense>
                    {draftFunctions.map(f => (
                      <ListItem
                        key={f.id}
                        selected={f.id === selectedFunctionId}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start'
                        }}
                      >
                        <Box sx={{
                          display: 'flex',
                          width: '100%',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <ListItemText
                            primary={f.name || f.id}
                            primaryTypographyProps={{ noWrap: true, title: f.name || f.id }}
                            onClick={() => handleSelectFunction(f.id)}
                            sx={{ cursor: 'pointer', flex: '1 1 auto', minWidth: 0, my: 0 }}
                          />
                          <Box sx={{
                            display: 'flex',
                            flexShrink: 0,
                            ml: 1,
                            gap: 0.25,
                            alignItems: 'center'
                          }}>
                            {f.invalid && (
                              <Tooltip title={f.invalid_reason || 'Function is invalid'}>
                                <Chip
                                  label="Invalid"
                                  color="warning"
                                  size="small"
                                  sx={{ mr: 0.5, height: 22 }}
                                />
                              </Tooltip>
                            )}
                            <IconButton size="small" onClick={() => handleEditFunction(f.id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleOpenDeleteFunction(f)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </ListItem>
                    ))}

                    {draftFunctions.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1, textAlign: 'center' }}>
                        No drafts available.
                      </Typography>
                    )}
                  </List>
                )}
              </Box>
            )}

            {/* Add New View/Function/Automation Button */}
            {newResourceAction && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={newResourceAction.onClick}
                  disabled={Boolean(newResourceAction.disabled)}
                  sx={{ mb: newResourceAction.error ? 0.5 : 2 }}
                >
                  {newResourceAction.label}
                </Button>
                {newResourceAction.error && (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 2 }}>
                    {newResourceAction.error}
                  </Typography>
                )}
              </>
            )}

            {/* Sidebar Footer */}
            <SidebarFooter page="dashboard" />
          </Box>
        </LeftSidebar>

        {/* Main Content - Takes up whatever the rails leave over */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          flexGrow: 1,
          flexBasis: 0,
          minWidth: 0,
        }}>
          <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 2,
          }}>
            {/* Selected, but the run target has not landed (or did not load). */}
            {functionPaneOpen && !functionDoc && (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {functionLoading ? (
                  <CircularProgress size={28} />
                ) : (
                  <Typography color="text.secondary">
                    {runError || 'This function could not be loaded.'}
                  </Typography>
                )}
              </Box>
            )}

            {/* Function home: the function, its run history, and the run on screen */}
            {functionPaneOpen && functionDoc && (
              <Paper
                sx={{
                  p: 2,
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  borderRadius: 1.5,
                  border: (theme) => `1px solid ${theme.palette.divider}`
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 500 }}>
                    {functionDoc.name || selectedFunctionId}
                  </Typography>
                  {isPublishedVersion(runnableVersion) && (
                    <Chip size="small" variant="outlined" color="success" label={`v${runnableVersion}`} />
                  )}
                  {functionDoc.invalid && (
                    <Tooltip title={functionDoc.invalid_reason || 'Function is invalid'}>
                      <Chip
                        icon={<WarningIcon />}
                        label="Invalid"
                        color="warning"
                        size="small"
                      />
                    </Tooltip>
                  )}
                  {isPublishedVersion(runnableVersion) && selectedHasUnpublishedChanges && (
                    <Chip size="small" variant="outlined" color="warning" label="Unpublished changes" />
                  )}

                  <Box sx={{ flexGrow: 1 }} />

                  {/* Run picker: this function's history in one dropdown. */}
                  <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={(event) => setRunMenuAnchor(event.currentTarget)}
                      endIcon={<ArrowDropDownIcon />}
                      sx={{ textTransform: 'none', borderColor: 'divider' }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography variant="caption" color="text.disabled">Run:</Typography>
                        <Typography variant="body2">
                          {viewedRunData
                            ? `${formatRunTime(viewedRunData.created_at)} · ${runOriginLabel(viewedRunData.trigger_type)}`
                            : 'Run history'}
                        </Typography>
                        {viewedRunData && <RunOutcomeGlyph outcome={viewedRunOutcome} fontSize={13} />}
                      </Stack>
                    </Button>
                  {!viewedRunData && functionRunsLoading && <CircularProgress size={18} />}

                  <Menu
                    anchorEl={runMenuAnchor}
                    open={Boolean(runMenuAnchor)}
                    onClose={() => setRunMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ sx: { minWidth: 290 } }}
                  >
                    {functionRuns.map((run) => (
                      <RunPickerRow
                        key={run.id}
                        run={run}
                        selected={run.id === viewedRunId}
                        onSelect={(selected) => {
                          setRunMenuAnchor(null);
                          paintRun(selected);
                        }}
                      />
                    ))}
                    {functionRunsError && (
                      <MenuItem disabled sx={{ whiteSpace: 'normal', maxWidth: 320 }}>
                        {loadFailureMessage('runs')}
                      </MenuItem>
                    )}
                    {functionRunsError && (
                      <MenuItem onClick={() => { setRunMenuAnchor(null); loadRunsForFunction(selectedFunctionId); }}>
                        Retry
                      </MenuItem>
                    )}
                    {functionRuns.length === 0 && !functionRunsError && (
                      <MenuItem disabled>
                        {functionRunsLoading ? 'Searching run history…' : 'No runs yet'}
                      </MenuItem>
                    )}
                    {functionRunsHasMore && (
                      <MenuItem
                        onClick={loadMoreFunctionRuns}
                        disabled={functionRunsLoading}
                        sx={{ justifyContent: 'center', color: 'text.secondary' }}
                      >
                        {functionRunsLoading ? <CircularProgress size={16} /> : 'older ›'}
                      </MenuItem>
                    )}
                  </Menu>

                  {viewedVersionMismatch && (
                    <Tooltip title="This run executed the version it pinned. Running the function now executes the currently published version.">
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={`ran v${viewedRunData.function_version} · current is v${runnableVersion}`}
                      />
                    </Tooltip>
                  )}

                  {/* A failed run still recorded its parameters (the run document is
                      written before execution), so it can be re-run like any other. */}
                  {viewedRunData && (
                    <Tooltip title={runDisabledReason || 'Prefill the parameter panel from this run and run it again'}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<RefreshIcon />}
                          onClick={handleRerunParameters}
                          disabled={
                            isLoading
                            || Boolean(runDisabledReason)
                            || runPreparing
                            || Boolean(pendingEmailRun)
                          }
                          sx={{ textTransform: 'none', borderColor: 'divider' }}
                        >
                          Re-run these parameters
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Stack>

                {paneRepublished && (
                  <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={(
                      <Button color="inherit" size="small" onClick={() => handleSelectFunction(selectedFunctionId)}>
                        Reload
                      </Button>
                    )}
                  >
                    {liveSelectedVersion
                      ? `This function was republished (v${liveSelectedVersion}) while this pane was open.`
                      : 'This function changed while this pane was open.'}
                    {' '}
                    {isPublishedVersion(runnableVersion)
                      ? `Runs from here still use v${runnableVersion} until you reload.`
                      : 'Reload to run it.'}
                  </Alert>
                )}

                {functionDoc.invalid && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {functionDoc.invalid_reason || 'This function references a deleted resource and cannot run.'}
                  </Alert>
                )}

                {!isPublishedVersion(runnableVersion) && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Publish this function to run it. Runs execute the published version, and this
                    function does not have one yet.
                  </Alert>
                )}

                {isPublishedVersion(runnableVersion) && selectedHasUnpublishedChanges && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Unpublished changes — runs, Automations and emails keep using
                    version {runnableVersion} until you publish the function again.
                  </Alert>
                )}

                {runError && (
                  <Alert severity="error" sx={{ mb:2 }}>{runError}</Alert>
                )}

                {resultHydrationError && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={(
                      <Button
                        color="inherit"
                        size="small"
                        onClick={handleRetryRunResult}
                      >
                        Retry
                      </Button>
                    )}
                  >
                    Decode could not load the complete saved result. The displayed preview may be incomplete.
                  </Alert>
                )}

                {runResult?.historySaveUnconfirmed && <RunHistorySaveWarning />}

                {/* A response whose history save failed still contains results.
                    Its warning replaces the receipt's persistence exception. */}
                {viewedRunFailed && viewedRunData && !runResult?.historySaveUnconfirmed && (
                  <>
                    <StageChips run={viewedRunData} />
                    {(viewedRunData.retrieval_status === 'failed' || viewedRunData.logic_status === 'failed') ? (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {(() => {
                          const failedRetrieval = viewedRunData.retrieval_status === 'failed';
                          const stageError = failedRetrieval
                            ? viewedRunData.stage_errors?.retrieval
                            : viewedRunData.stage_errors?.logic;
                          // The run record names the failing block; resolve it
                          // through the pinned version's block names (M206).
                          const blockName = stageError?.block_id ? runBlockNames?.[stageError.block_id] : null;
                          return (
                            <>
                              This run failed during the {failedRetrieval ? 'data retrieval' : 'logic'} stage
                              {blockName ? ` in block "${formatText(blockName)}"` : ''}.
                              {' '}Please contact support and quote run ID <strong>{viewedRunData.id}</strong>.
                              {stageError?.message ? ` (${stageError.message})` : ''}
                            </>
                          );
                        })()}
                      </Alert>
                    ) : viewedRunData.error ? (
                      <Alert severity="error" sx={{ mb: 2 }}>{viewedRunData.error}</Alert>
                    ) : (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        This run failed. Please contact support and quote run ID <strong>{viewedRunData.id}</strong>.
                      </Alert>
                    )}
                    {/* The failure alone would hide an email that had already gone
                        out. A run with a result shows it in its Email tab instead. */}
                    {submittedEmailReceipts(viewedRunData).map((receipt) => (
                      <Alert key={receipt.outputBlockId} severity="warning" sx={{ mb: 2 }}>{receipt.sentence}</Alert>
                    ))}
                  </>
                )}

                {/* A partial or warning outcome still produced results, so the
                    stage chips sit above them rather than replacing them. */}
                {!viewedRunFailed && (viewedRunOutcome === 'partial_success' || viewedRunOutcome === 'warning') && viewedRunData && (
                  <StageChips run={viewedRunData} />
                )}

                {runResult?.truncatedTables?.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {runResult.truncatedTables.join(', ')} returned more than 50,000 rows, so this result was computed from the first 50,000 rows only - narrow the function's filters or set a date range for complete results.
                  </Alert>
                )}

                <Box sx={{ mb:2, flexGrow: 1, overflow: 'auto' }}>
                  {resultLoading && !runResult ? (
                    <Box sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#21252E',
                      borderRadius: 1
                    }}>
                      <CircularProgress />
                    </Box>
                  ) : runResult ? (
                    renderFunctionResult({
                      runId: viewedRunId,
                      functionId: selectedFunctionId,
                      // The live run document is authoritative for Output state,
                      // including Email delivery and projection watermarks.
                      summaryMap: viewedRunData?.id === viewedRunId ? viewedRunData?.outputs_summary : null,
                    })
                  ) : (
                    <Box sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#21252E',
                      borderRadius: 1
                    }}>
                      {functionRunsError ? (
                        <LoadFailure thing="run history" onRetry={() => loadRunsForFunction(selectedFunctionId)} />
                      ) : <Typography variant="body1" color="text.secondary">
                        {viewedRunFailed
                          ? 'No output was produced for this run.'
                          : functionRunsLoading
                            ? 'Searching run history…'
                            : 'This function has not completed a run yet — run it to see results here.'}
                      </Typography>}
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {activeTab === TAB_VIEWS && selectedViewId && viewData && (
              <ViewDisplay
                key={`${viewData.id}:${viewLoadGeneration}`}
                latestView={views.find((view) => view.id === viewData.id) || null}
                onReload={() => handleSelectView(viewData.id)}
                viewData={viewData}
                functions={functions.filter((resource) => !isResourceDeleting(resource))}
              />
            )}

            {automationPaneOpen && (
              <AutomationEditor
                // Keyed so every piece of editor state — an error, a dialog, a
                // draft, the edit lease — belongs to one automation and resets
                // (and releases) when another is selected.
                key={selectedAutomation.id}
                companyName={companyName}
                automation={selectedAutomation}
                target={automationTarget}
                targetError={automationTargetError}
                targetLoading={automationTargetLoading}
                onRetryTarget={() => {
                  retryResources();
                  return handleSelectAutomation(selectedAutomation);
                }}
                tables={tables}
                contacts={automationContacts}
                actorUid={currentUser?.uid || 'unknown'}
                actorName={currentUser?.displayName || currentUser?.email || 'Another user'}
                onChanged={handleAutomationChanged}
                onDeleted={handleAutomationDeleted}
              />
            )}

            {activeTab === TAB_ACTIVITY && (
              <ActivityFeed
                functionNameById={functionNameById}
                // A complete probe is the whole filtered history; runs the live
                // head has since delivered for that origin are folded in.
                runs={activityProbeComplete
                  ? mergeActivityHead(activityProbe.runs, activityRuns.filter((run) => run.trigger_type === activityProbe.origin))
                  : activityRuns}
                loading={activityLoading || Boolean(activityProbe?.loading)}
                error={activityError}
                onRetry={() => loadActivity()}
                hasMore={activityProbeComplete ? false : activityHasMore}
                onLoadMore={() => loadActivity(activityLastDoc)}
                searching={activitySearching}
                onSelectRun={handleSelectActivityRun}
                originFilter={activityOriginFilter}
                outcomeFilter={activityOutcomeFilter}
                onOriginFilter={setActivityOriginFilter}
                onOutcomeFilter={setActivityOutcomeFilter}
              />
            )}

            {activeTab !== TAB_ACTIVITY && !functionPaneOpen && !selectedViewId && !automationPaneOpen && (
              activeTab === TAB_VIEWS ? (
                <CentrePaneZeroState
                  icon={<DashboardIcon sx={{ fontSize: 44, color: 'text.disabled' }} />}
                  heading="No view open"
                  copy="A view arranges the results of your functions side by side on one dashboard. Choose one from the sidebar, or build your first."
                  actionLabel="New View"
                  onAction={handleCreateView}
                />
              ) : activeTab === TAB_AUTOMATIONS ? (
                <CentrePaneZeroState
                  icon={<BoltIcon sx={{ fontSize: 44, color: 'text.disabled' }} />}
                  heading="No automation open"
                  copy="Automations run your Functions on a schedule or when new data arrives. Choose one from the sidebar, or set up your first."
                  actionLabel="New Automation"
                  onAction={() => setShowCreateAutomation(true)}
                />
              ) : (
                <CentrePaneZeroState
                  icon={<FunctionsIcon sx={{ fontSize: 44, color: 'text.disabled' }} />}
                  heading="No function open"
                  copy="A function turns your tables into an answer you can run again whenever you need it. Choose one from the sidebar, or build your first."
                  actionLabel="New Function"
                  onAction={handleCreateFunction}
                  disabled={creatingFunction}
                />
              )
            )}
          </Box>
        </Box>

        {/* Right Parameters Panel */}
        {functionPaneOpen && functionDoc && (
          <Box sx={{
            height: '100%',
            display: { xs: 'none', md: 'block' },
            position: 'relative',
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
            width: paramsWidth,
            maxWidth: paramsWidth,
            flexShrink: 0,
            ml: 'auto',
          }}>
            {paramsCollapsed && (
              <Box sx={{ pt: 1.5, display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Expand parameters" placement="left">
                  <IconButton size="small" onClick={() => setParamsCollapsed(false)}>
                    <KeyboardDoubleArrowLeftIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Box sx={{
              p: 2,
              pr: 2,
              height: '100%',
              display: paramsCollapsed ? 'none' : 'block',
            }}>
              <DateDraftScope ref={dateScopeRef} onPartialChange={handleDateDraftChange}>
              <Paper sx={{
                p: 2,
                height: '100%',
                borderRadius: 1.5,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 1,
                mr: 1,
              }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ color: 'text.primary' }}>
                    Parameters
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={unifyParameters
                      ? 'One control per column, applied to every data source that reads it'
                      : 'Each data source edited on its own'}>
                      <FormControlLabel
                        sx={{ ml: 0, mr: 0 }}
                        control={(
                          <Switch
                            size="small"
                            checked={unifyParameters}
                            onChange={(e) => setUnifyParameters(e.target.checked)}
                            inputProps={{ 'aria-label': 'Unify parameters across data sources' }}
                          />
                        )}
                        label={<Typography variant="caption">Unify</Typography>}
                      />
                    </Tooltip>
                    <Tooltip title="Collapse parameters">
                      <IconButton size="small" onClick={() => setParamsCollapsed(true)}>
                        <KeyboardDoubleArrowRightIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box key={parameterClearGeneration} sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
                  {unifyParameters ? (
                    unifiedParameters.map((param) => {
                      const col = param.name;
                      // Every block holds this value: a column the blocks hold
                      // differently is listed per source below, never merged.
                      const [firstValue = []] = Object.values(param.valuesByBlock || {});
                      return (
                        <ParameterField
                          key={col}
                          name={col}
                          error={preparedParameters.errors.find((issue) => (
                            issue.column === col && issue.blockId in param.valuesByBlock
                          ))?.message || ''}
                          value={firstValue}
                          onChange={(next) => handleUnifiedParameterChange(col, next)}
                          type={param.type}
                          isRange={param.isRange}
                          isDateRange={param.isDateRange}
                          isAttribute={param.isAttribute}
                          availableValues={attributeValues[col]}
                          indexedTableIds={indexedTableIdsFor(col, param.type, param.tableIds)}
                          disabled={!param.anyVariable}
                          excluded={isUnifiedExcluded(param)}
                          onToggleExclude={() => handleUnifiedToggleExclude(param)}
                        />
                      );
                    })
                  ) : (
                    blockParameters.map(renderBlockGroup)
                  )}
                  {unifyParameters && separateBlockParameters.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        Set per data source — these columns differ between the sources.
                      </Typography>
                      {separateBlockParameters.map(renderBlockGroup)}
                    </Box>
                  )}
                  {unifyParameters && differingBlockParameters.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        Set per data source — the sources currently hold different values.
                      </Typography>
                      {differingBlockParameters.map(renderBlockGroup)}
                    </Box>
                  )}
                </Box>

                {dateDraftError && (
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    {dateDraftError}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleClearParameters}
                    disabled={!hasActiveParameters}
                    sx={{ flexShrink: 0 }}
                  >
                    Clear
                  </Button>
                  <Tooltip title={runDisabledReason}>
                    <span style={{ flexGrow: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleRunFunction}
                        fullWidth
                        disabled={
                          isLoading
                          || Boolean(runDisabledReason)
                          || runPreparing
                          || Boolean(pendingEmailRun)
                        }
                      >
                        {runPreparing ? 'Preparing…' : 'Run All'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Paper>
              </DateDraftScope>
            </Box>
          </Box>
        )}
      </Box>

      {functionDoc && showBuilder && (
        <FunctionBuilderDialog
          open={showBuilder}
          sessionId={builderSession.sessionId}
          onClose={async () => {
            closeBuilder();
            // The builder may have published, so reload the head pointer and the
            // version the run surfaces read from.
            await refreshFunctions();
            if (selectedFunctionId) await handleSelectFunction(selectedFunctionId);
          }}
          onSave={(funcInfo) => {
            closeBuilder();
            handleOpenNameDialog(funcInfo);
          }}
          tables={tables}
          companyName={companyName}
          functionId={selectedFunctionId}
        />
      )}

      {showCreateAutomation && (
        <CreateAutomationDialog
          open={showCreateAutomation}
          onClose={() => setShowCreateAutomation(false)}
          companyName={companyName}
          functions={functions.filter((resource) => !isResourceDeleting(resource))}
          tables={tables}
          actorUid={currentUser?.uid || 'unknown'}
          onCreated={handleAutomationCreated}
        />
      )}

      {showViewBuilder && (
        <ViewBuilder
          open={showViewBuilder}
          sessionId={viewBuilderSession.sessionId}
          onClose={() => closeViewBuilder()}
          viewId={selectedViewId}
          companyName={companyName}
          functions={functions.filter((resource) => !isResourceDeleting(resource))}
          onViewSaved={handleViewSaved}
        />
      )}

      <DeleteResourceDialog
        open={Boolean(functionDeletion.target)}
        title={`Delete ${functionDeletion.target?.name || functionDeletion.target?.id || 'Function'}`}
        preview={functionDeletion.preview}
        scopeChanged={functionDeletion.scopeChanged}
        executionError={functionDeletion.executionError}
        resourceLabel="function"
        onConfirm={handleDeleteFunction}
        onClose={functionDeletion.close}
        busy={functionDeletion.busy}
      />

      <DeleteResourceDialog
        open={Boolean(viewDeletion.target)}
        title={`Delete ${viewDeletion.target?.name || viewDeletion.target?.id || 'View'}`}
        preview={viewDeletion.preview}
        scopeChanged={viewDeletion.scopeChanged}
        executionError={viewDeletion.executionError}
        resourceLabel="view"
        onConfirm={viewDeletion.confirm}
        onClose={viewDeletion.close}
        busy={viewDeletion.busy}
      />

      <Dialog open={nameDialogOpen} onClose={() => !nameSaving && setNameDialogOpen(false)}>
        <DialogTitle>Name Function</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Function Name"
            fullWidth
            value={newFunctionName}
            onChange={(e) => {
              setNewFunctionName(e.target.value);
              setNameError('');
            }}
            disabled={nameSaving}
            error={Boolean(nameError)}
            helperText={nameError || ' '}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialogOpen(false)} disabled={nameSaving}>Cancel</Button>
          <Button
            onClick={handleNameSave}
            disabled={!newFunctionName.trim() || nameSaving}
            startIcon={nameSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {nameError ? 'Retry' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {publishingFunctionId && (
        <PublishFunctionDialog
            open={Boolean(publishingFunctionId)}
            companyName={companyName}
            functionId={publishingFunctionId}
            actorUid={currentUser?.uid || 'unknown'}
            onClose={async () => {
                setPublishingFunctionId(null);
                await refreshFunctions();
                if (selectedFunctionId) await handleSelectFunction(selectedFunctionId);
            }}
        />
      )}

      <EffectConfirmationDialog
        open={Boolean(pendingEmailRun)}
        confirmation={pendingEmailRun?.confirmation}
        changed={Boolean(pendingEmailRun?.changed)}
        error={pendingEmailRun?.error || ''}
        busy={runConfirmBusy}
        onConfirm={handleConfirmEmailRun}
        onCancel={handleCancelEmailRun}
      />

      <RetryEmailDialog
        open={Boolean(retryRequest)}
        detail={retryRequest?.detail}
        error={retryRequest?.error}
        busy={runConfirmBusy}
        onConfirm={handleConfirmRetry}
        onCancel={handleCancelRetry}
      />
    </Box>
  );
}

export default Dashboard;
